import React, { useState, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import CardPayrollSalaryTabUX from "./CardPayrollSalaryTabUX";
import { Input, Button, Icon, Modal, Dropdown, Table, Checkbox, TextArea } from "semantic-ui-react";
import { DateTextBox } from "react-lib/apps/common";
import { RunSequence, SetProp, BTN_ACTS, CARD_NAME } from "./sequence/Payroll";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import ModInfo from "react-lib/apps/common/ModInfo";

type CardPayrollSalaryTabProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  buttonLoadCheck?: any;
  errorMessage?: any;
  successMessage?: any;
  PayrollSequence: any;
  runSequence: RunSequence;
  masterOptions: any;
  onRowSelect?: any;
  languageUX?: any;
};

const CardPayrollSalaryTab = (props: CardPayrollSalaryTabProps) => {
  console.log("Payroll Salary Tab props:", props);

  useEffect(() => {
    props.runSequence({ sequence: "Payroll", restart: true });
  }, []);

  const getSalaryTabStyle = (targetType: "CREATESALARY" | "SALARYPAYRUN") => ({
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
    padding: "10px 16px",
    fontWeight: "bold",
    backgroundColor: activeSalaryTab === targetType ? "#2185d0" : undefined,
    color: activeSalaryTab === targetType ? "white" : undefined,
    borderBottom: "none",
  });

  const employeeList = props.PayrollSequence?.employeePayrollList ?? [];
  const summary = props.PayrollSequence?.payrollSummaryParams ?? {};
  const salaryPayRunList = props.PayrollSequence?.salaryPayRunList ?? [];
  const payrollRecordList = props.PayrollSequence?.payrollRecordList ?? [];
  const payrollRecordMap = new Map(
    (payrollRecordList as any[]).map((record) => [record.id, record])
  );

  const employeesWithOtCutoff = employeeList.map((emp: any) => {
    const record: any = payrollRecordMap.get(emp.payroll_record);
    return {
      ...emp,
      ot_cut_off: record?.ot_cut_off ?? null,
    };
  });

  const [activeEditSalaryTab, setActiveEditSalaryTab] = useState<EditSalaryTab>("SALARY_DETAIL");
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<number | null>(null);
  const [bankPaymentDigits, setBankPaymentDigits] = useState(Array(10).fill(""));
  const [paidViaDigits, setPaidViaDigits] = useState(Array(10).fill(""));

  const onChangeBankPayment = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const input = e.target.value;
    const digit = input.replace(/\D/g, "").slice(0, 1);

    const updated = [...bankPaymentDigits];
    updated[index] = digit;
    setBankPaymentDigits(updated);

    if (digit && index < 19) {
      const nextInput = document.getElementById(
        `BankPayment-Input-${index + 1}`
      ) as HTMLInputElement | null;
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const onKeyDownBankPayment = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && bankPaymentDigits[index] === "" && index > 0) {
      const prevInput = document.getElementById(
        `BankPayment-Input-${index - 1}`
      ) as HTMLInputElement | null;
      if (prevInput) {
        prevInput.focus();
        const updated = [...bankPaymentDigits];
        updated[index - 1] = "";
        setBankPaymentDigits(updated);
        e.preventDefault();
      }
    }
  };

  const onChangePaidVia = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const input = e.target.value;
    const digit = input.replace(/\D/g, "").slice(0, 1);

    const updated = [...paidViaDigits];
    updated[index] = digit;
    setPaidViaDigits(updated);

    if (digit && index < 19) {
      const nextInput = document.getElementById(
        `PaidVia-Input-${index + 1}`
      ) as HTMLInputElement | null;
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const onKeyDownPaidVia = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && paidViaDigits[index] === "" && index > 0) {
      const prevInput = document.getElementById(
        `PaidVia-Input-${index - 1}`
      ) as HTMLInputElement | null;
      if (prevInput) {
        prevInput.focus();
        const updated = [...paidViaDigits];
        updated[index - 1] = "";
        setPaidViaDigits(updated);
        e.preventDefault();
      }
    }
  };

  const handleBankPaymentChange = (_: any, { value }: any) => {
    setBankAccount(value);
    setSelectedBankAccountId(value);

    const selectedAccount = props.PayrollSequence?.bankAccount?.find(
      (acc: any) => acc.id === value
    );

    if (selectedAccount?.bank_account_number) {
      const digitsArray = selectedAccount.bank_account_number
        .replace(/\D/g, "")
        .slice(0, 20)
        .split("");
      setBankPaymentDigits(digitsArray);
    } else {
      setBankPaymentDigits([]);
    }
  };

  const handlePaidViaChange = (_: any, { value }: any) => {
    setPaidViaBank(value);
    setSelectedBankAccountId(value);

    const selectedAccount = props.PayrollSequence?.bankAccount?.find(
      (acc: any) => acc.id === value
    );

    if (selectedAccount?.bank_account_number) {
      const digitsArray = selectedAccount.bank_account_number
        .replace(/\D/g, "")
        .slice(0, 20)
        .split("");
      setPaidViaDigits(digitsArray);
    } else {
      setPaidViaDigits([]);
    }
  };

  const formatToBackendDate = (thaiDate: string): string => {
    const parts = thaiDate.split("/");
    if (parts.length !== 3) {
      return "";
    }
    const [day, month, year] = parts;
    const gregorianYear = parseInt(year) - 543;

    return moment(`${gregorianYear}-${month}-${day}`, "YYYY-MM-DD").format("YYYY-MM-DD");
  };

  const [activeSalaryTab, setActiveSalaryTab] = useState<"CREATESALARY" | "SALARYPAYRUN">(
    "CREATESALARY"
  );

  type OvertimeDetail = {
    hour: number;
    factor: number;
    baseOT: number;
  };

  const formatNumber = (value: number | string | undefined | null): string => {
    const num = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;

    if (isNaN(num as number) || num == null) {
      return "-";
    }

    return (num as number).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const baseSalary = [
    {
      approveDate: "28/01/2025",
      employeeId: "MSK00011",
      fullname: "Mrs. Kochamon Kan",
      position: "Doctor",
      department: "Medicine",
      salary: 70000.0,
      bonus: 0.0,
      doctorFee: 35000.0,
      providentFund: 0.0,
      absence: 0.0,
      withholdingTax: -1000.0,
      socialSecFund: -750.0,
      overtimeDetails: [
        { hour: 0, factor: 1.0, baseOT: 250 },
        { hour: 0, factor: 1.0, baseOT: 250 },
        { hour: 0, factor: 2.0, baseOT: 250 },
      ],
    },
    {
      approveDate: "25/01/2025",
      employeeId: "MSK00124",
      fullname: "Ms. Malinee Gerd",
      position: "Nurse",
      department: "Medicine",
      salary: 45000.0,
      bonus: 0.0,
      doctorFee: 0.0,
      providentFund: -900.0,
      absence: 0.0,
      withholdingTax: -1514.17,
      socialSecFund: -750.0,
      overtimeDetails: [
        { hour: 4, factor: 1.0, baseOT: 2000 },
        { hour: 2.4, factor: 1.0, baseOT: 2000 },
        { hour: 1.2, factor: 2.0, baseOT: 2000 },
      ],
    },
  ];

  const salaryDataMap = {
    "PR-001": [
      {
        approveDate: "15/01/2025",
        employeeId: "MSK00009",
        fullname: "Mr. Somchai W.",
        position: "Technician",
        department: "Maintenance",
        salary: 40000.0,
        bonus: 3000.0,
        doctorFee: 0.0,
        providentFund: -800.0,
        absence: -500.0,
        withholdingTax: -900.0,
        socialSecFund: -750.0,
        overtimeDetails: [
          { hour: 1, factor: 1.0, baseOT: 200 },
          { hour: 0.5, factor: 1.5, baseOT: 200 },
          { hour: 1, factor: 2.0, baseOT: 200 },
        ],
      },
    ],
    "PR-002": baseSalary,
    "PR-003": [
      {
        approveDate: "05/01/2025",
        employeeId: "MSK00188",
        fullname: "Mr. Anon Dee",
        position: "Pharmacist",
        department: "Pharmacy",
        salary: 50000,
        bonus: 2000,
        doctorFee: 0,
        providentFund: -1000,
        absence: -200,
        withholdingTax: -950,
        socialSecFund: -750,
        overtimeDetails: [
          { hour: 2, factor: 1.0, baseOT: 300 },
          { hour: 1, factor: 2.0, baseOT: 300 },
        ],
      },
    ],
    "PR-004": [
      {
        approveDate: "10/01/2025",
        employeeId: "MSK00035",
        fullname: "Ms. Jariya Lam",
        position: "Accountant",
        department: "Finance",
        salary: 48000,
        bonus: 1000,
        doctorFee: 0,
        providentFund: -950,
        absence: 0,
        withholdingTax: -890,
        socialSecFund: -750,
        overtimeDetails: [{ hour: 1.5, factor: 1.5, baseOT: 300 }],
      },
    ],
    "PR-005": [
      {
        approveDate: "12/01/2025",
        employeeId: "MSK00076",
        fullname: "Mr. Niran B.",
        position: "Receptionist",
        department: "Front Office",
        salary: 30000,
        bonus: 500,
        doctorFee: 0,
        providentFund: -500,
        absence: 0,
        withholdingTax: -500,
        socialSecFund: -750,
        overtimeDetails: [{ hour: 0, factor: 1.0, baseOT: 250 }],
      },
    ],
  };

  const CreateSalaryData = Object.entries(salaryDataMap).flatMap(([payRunCode, list]) =>
    list.map((item) => {
      const overtime =
        item.overtimeDetails?.reduce(
          (total, row) => total + row.hour * row.factor * row.baseOT,
          0
        ) || 0;

      const totalRevenue = item.salary + overtime + item.bonus + item.doctorFee;
      const totalDeduct =
        item.providentFund + item.absence + item.withholdingTax + item.socialSecFund;
      const netSalary = totalRevenue + totalDeduct;

      return {
        ...item,
        payRunCode,
        issuedDate: {
          "PR-001": "01/06/2025",
          "PR-002": "01/07/2025",
          "PR-003": "01/08/2025",
          "PR-004": "01/09/2025",
          "PR-005": "01/10/2025",
        }[payRunCode],

        status: {
          "PR-001": "Paid",
          "PR-002": "Wait Approve",
          "PR-003": "Wait Final Approve",
          "PR-004": "Outstanding",
          "PR-005": "Draft",
        }[payRunCode] as SalaryStatus,
        overtime,
        totalRevenue,
        totalDeduct,
        netSalary,
      };
    })
  );

  const groupedPayRuns = Object.values(
    CreateSalaryData.reduce((acc, item) => {
      if (!acc[item.payRunCode]) {
        acc[item.payRunCode] = {
          code: item.payRunCode,
          issuedDate: item.issuedDate,
          cycleStartDate: item.issuedDate,
          cycleEndDate: item.issuedDate,
          paymentDate: item.status === "PAID" ? item.issuedDate : "",
          employees: 0,
          amount: 0,
          status: item.status,
        };
      }
      acc[item.payRunCode].employees += 1;
      acc[item.payRunCode].amount += item.netSalary;
      return acc;
    }, {} as Record<string, any>)
  ).map((item) => ({
    ...item,
    amount: formatNumber(item.amount),
  }));

  const [openOvertimeModal, setOpenOvertimeModal] = useState(false);

  const getStatusStyle = (status?: string): React.CSSProperties => {
    const normalizedStatus = status?.toUpperCase() ?? "";

    let backgroundColor = "red";

    switch (normalizedStatus) {
      case "DRAFT":
        backgroundColor = "orange";
        break;
      case "WAIT_APPROVE":
        backgroundColor = "red";
        break;
      case "WAIT_FINAL_APPROVE":
        backgroundColor = "red";
        break;
      case "OUTSTANDING":
        backgroundColor = "#2aa9e0";
        break;
      case "PAID":
        backgroundColor = "#4caf50";
        break;
    }

    return {
      color: "white",
      backgroundColor,
      borderRadius: "6px",
      padding: "5px 15px",
      fontWeight: "bold",
      display: "inline-block",
      minWidth: "200px",
      textAlign: "center",
    };
  };

  const statusTextMap: Record<string, string> = {
    DRAFT: "Draft",
    WAIT_APPROVE: "Wait Approve",
    WAIT_FINAL_APPROVE: "Wait Final Approve",
    OUTSTANDING: "Outstanding",
    PAID: "Paid",
  };

  const [editingCell, setEditingCell] = React.useState<{ id: number; field: string } | null>(null);
  const [editValue, setEditValue] = React.useState<string>("");
  const [version, setVersion] = useState(0);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const allColumnWidthsRef = useRef<{ [key: number]: number }>({});
  const [renderTrigger, setRenderTrigger] = useState(0);

  const salaryItemsTable = (
    employeeData: any[] = [],
    currentStatus: SalaryStatus = "DRAFT",
    summary?: any,
    selectedSalaryPayRunDueDate: string = ""
  ) => {
    const canEdit =
      activeSalaryTab === "CREATESALARY" ||
      selectedStatus === "DRAFT" ||
      selectedStatus === "WAIT_APPROVE";
    const paySlip = selectedStatus === "OUTSTANDING" || selectedStatus === "PAID";
    const positionOptions = props.masterOptions?.positionAll ?? [];
    const dataToDisplay =
      employeeData && employeeData.length >= 0 ? employeeData : employeeList ?? [];
    const allPayrollItems = dataToDisplay.flatMap((emp: any) =>
      Array.isArray(emp.payroll_items) ? emp.payroll_items : []
    );
    const seen = new Set();
    const uniquePayrollItems = allPayrollItems.filter((item: any) => {
      const nameKey = props.languageUX === "en" ? item.name_en : item.name_th;
      const key = nameKey + item.type;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    const dynamicPayrollItems = uniquePayrollItems.sort((a: any, b: any) => {
      if (a.type === b.type) {
        return 0;
      }
      return a.type === "revenue" ? -1 : 1;
    });
    console.log("Dynamic Payroll Items:", dynamicPayrollItems);

    const headerStyle = {
      backgroundColor: "#add8d6",
      border: "1px solid #82c8e5",
      whiteSpace: "nowrap",
      width: "1%",
      boxSizing: "border-box" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    const redHeaderStyle = { ...headerStyle, color: "red" };
    const cellStyle = {
      whiteSpace: "nowrap",
      padding: "4px 8px",
      textAlign: "right",
      boxSizing: "border-box",
    };
    const editableCellStyle = {
      backgroundColor: "#fff9db",
    };

    const formatPaySlipFileName = (date: string) => {
      const [day, month, year] = date.split("/");

      const monthMap: { [key: string]: string } = {
        "01": "Jan",
        "02": "Feb",
        "03": "Mar",
        "04": "Apr",
        "05": "May",
        "06": "Jun",
        "07": "Jul",
        "08": "Aug",
        "09": "Sep",
        "10": "Oct",
        "11": "Nov",
        "12": "Dec",
      };

      const monthName = monthMap[month];

      return `${year}_${monthName}_${day}.pdf`;
    };

    const getStickyStyle = (index: number, isHeader = false, hovered = false) => {
      let left = 0;
      for (let i = 0; i < index; i++) {
        left += allColumnWidthsRef.current[i] || 0;
      }

      const width = allColumnWidthsRef.current[index] || 150;

      return {
        position: "sticky" as const,
        left,
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        backgroundColor: isHeader ? "#add8d6" : hovered ? "#eaf6ff" : "#ffffff",
        zIndex: isHeader ? 4 : 3,
        border: isHeader ? "1px solid #82c8e5" : undefined,
        boxSizing: "border-box",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };
    };

    const dynamicHeaders = dynamicPayrollItems.map((item: any) => ({
      title: props.languageUX === "en" ? item.name_en : item.name_th,
      style: item.type === "deduct" ? redHeaderStyle : headerStyle,
    }));

    const summaryHeaders = [
      { title: "Total Revenue", style: headerStyle },
      { title: "Total Deduct", style: headerStyle },
      { title: "Net Salary", style: headerStyle },
    ];

    const preDynamicHeaders = [
      { title: "Salary", style: headerStyle },
      { title: "Overtime", style: headerStyle },
      { title: "Position Pay", style: headerStyle },
      { title: "License Allowance", style: headerStyle },
      { title: "Language Pay", style: headerStyle },
      { title: "Level Skill", style: headerStyle },
      { title: "Bonus", style: headerStyle },
      { title: "Doctor Fee", style: headerStyle },
      { title: "Provident Fund", style: redHeaderStyle },
      { title: "Absence", style: redHeaderStyle },
      { title: "Withholding Tax", style: redHeaderStyle },
      { title: "Social Security Fund", style: redHeaderStyle },
    ];

    const paySlipHeaders: { title: string; style: React.CSSProperties }[] = [];
    if (paySlip) {
      paySlipHeaders.push({ title: "Bilingual Pay slip", style: headerStyle });
    }

    const allNonFrozenHeaders = [
      ...preDynamicHeaders,
      ...dynamicHeaders,
      ...summaryHeaders,
      ...paySlipHeaders,
    ];

    const frozenColumnWidths: number[] = [155, 150, 150, 150, 150];

    const totalWidth = frozenColumnWidths.reduce((a, b) => a + b, 0);
    const totalWidthPx = `${totalWidth}px`;

    if (Object.keys(allColumnWidthsRef.current).length === 0) {
      for (let i = 0; i < frozenColumnWidths.length; i++)
        allColumnWidthsRef.current[i] = frozenColumnWidths[i];

      allNonFrozenHeaders.forEach((_, idx) => {
        allColumnWidthsRef.current[idx + frozenColumnWidths.length] = 150;
      });
    }

    function ResizableHeader({
      index,
      children,
      style,
      minWidth = 50,
    }: {
      index: number;
      children: React.ReactNode;
      style?: React.CSSProperties;
      minWidth?: number;
    }) {
      const [width, setWidth] = useState(allColumnWidthsRef.current[index] || 150);

      const startResize = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = allColumnWidthsRef.current[index];

        const onMouseMove = (moveEvent: MouseEvent) => {
          const diff = moveEvent.clientX - startX;
          const newWidth = Math.max(startWidth + diff, minWidth);

          allColumnWidthsRef.current[index] = newWidth;
          setWidth(newWidth);

          setRenderTrigger((prev) => prev + 1);
        };

        const onMouseUp = () => {
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
      };

      return (
        <Table.HeaderCell
          id={`column-header-${index}`}
          style={{
            ...style,
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
            position: "relative",
          }}
        >
          {children}
          <div
            style={{
              width: "5px",
              cursor: "col-resize",
              height: "100%",
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 10,
            }}
            onMouseDown={startResize}
          />
        </Table.HeaderCell>
      );
    }

    const startResize = (index: number, startX: number) => {
      const startWidth = allColumnWidthsRef.current[index];
      const minWidth = index < frozenColumnWidths.length ? frozenColumnWidths[index] : 50;

      const onMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(startWidth + diff, minWidth);

        allColumnWidthsRef.current[index] = newWidth;

        setRenderTrigger((prev) => prev + 1);
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    };

    console.log(employeeData, employeeList);
    const paddedData = [...dataToDisplay];
    while (paddedData.length < 15) {
      paddedData.push({ isEmpty: true });
    }

    const saveEdit = (item: any, field: string, payItem?: any) => {
      let newValue = editValue.trim().replace(/,/g, "");

      if (isNaN(Number(newValue))) {
        alert("Please enter a valid number");
        setEditingCell(null);
        setEditValue("");
        return;
      }

      const numericValue = parseFloat(newValue);

      const sanitizedItem: { [key: string]: number } = {
        salary: parseFloat(String(item.salary).replace(/,/g, "")) || 0,
        overtime: parseFloat(String(item.overtime).replace(/,/g, "")) || 0,
        bonus: parseFloat(String(item.bonus).replace(/,/g, "")) || 0,
        position_pay: parseFloat(String(item.position_pay).replace(/,/g, "")) || 0,
        license_allowance: parseFloat(String(item.license_allowance).replace(/,/g, "")) || 0,
        language_pay: parseFloat(String(item.language_pay).replace(/,/g, "")) || 0,
        skill_pay: parseFloat(String(item.skill_pay).replace(/,/g, "")) || 0,
        doctor_fee: parseFloat(String(item.doctor_fee).replace(/,/g, "")) || 0,
        providence_fund: parseFloat(String(item.providence_fund).replace(/,/g, "")) || 0,
        with_holding_tax: parseFloat(String(item.with_holding_tax).replace(/,/g, "")) || 0,
        social_security_fund: parseFloat(String(item.social_security_fund).replace(/,/g, "")) || 0,
        absence: parseFloat(String(item.absence).replace(/,/g, "")) || 0,
      };

      if (sanitizedItem.hasOwnProperty(field)) {
        sanitizedItem[field] = numericValue;
      }

      let updatedPayrollItems = item.payroll_items || [];

      if (payItem) {
        const updated = [...updatedPayrollItems];
        const idx = updated.findIndex((pi) => pi.name_en === payItem.name_en);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], amount: numericValue };
        } else {
          updated.push({ ...payItem, amount: numericValue });
        }
        updatedPayrollItems = updated;
      }

      const updatedItem = {
        ...item,
        ...sanitizedItem,
        payroll_items: updatedPayrollItems,
      };

      handleEditEmployeePayroll(null, { value: numericValue }, updatedItem);
      const salaryPayRunId = item.salary_pay_run || props?.PayrollSequence?.salaryPayRun;
      if (salaryPayRunId) {
        props.runSequence({
          sequence: "Payroll",
          action: "HANDLE_SEARCH_EMPLOYEE_PAYROLL",
          salaryPayRun: salaryPayRunId,
        });
      }
      setTimeout(() => {
        setVersion((v) => v + 1);
      }, 0);
      setEditingCell(null);
    };

    return (
      <div id="table-scroll-container" style={{ overflowX: "auto", width: "100%" }}>
        <Table
          celled
          compact
          style={{
            tableLayout: "fixed",
            width: "max-content",
            borderCollapse: "separate",
          }}
        >
          <Table.Header>
            <Table.Row>
              {["Date Approve Payroll", "Employee ID", "Name", "Position", "Department"].map(
                (title, idx) => (
                  <Table.HeaderCell
                    key={idx}
                    id={`column-header-${idx}`}
                    style={getStickyStyle(idx, true)}
                  >
                    {title}
                    <div
                      style={{
                        width: "5px",
                        cursor: "col-resize",
                        height: "100%",
                        position: "absolute",
                        right: 0,
                        top: 0,
                        zIndex: 10,
                      }}
                      onMouseDown={(e) => startResize(idx, e.clientX)}
                    />
                  </Table.HeaderCell>
                )
              )}

              {allNonFrozenHeaders.map((col, idx) => (
                <ResizableHeader key={idx + 5} index={idx + 5} style={col.style}>
                  {col.title === "Bilingual Pay slip" ? (
                    <>
                      <i className="download icon" style={{ marginRight: 5 }} />
                      {col.title}
                    </>
                  ) : (
                    col.title
                  )}
                </ResizableHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paddedData.map((item: any, index) =>
              item.isEmpty ? (
                <Table.Row key={`empty-${index}`}>
                  {Array.from({
                    length: (paySlip ? 21 : 20) + dynamicPayrollItems.length,
                  }).map((_, idx) => (
                    <Table.Cell
                      key={`empty-${idx}`}
                      style={idx < frozenColumnWidths.length ? getStickyStyle(idx) : {}}
                    >
                      &nbsp;
                    </Table.Cell>
                  ))}
                </Table.Row>
              ) : (
                <Table.Row
                  key={item.id ?? index}
                  onClick={() => props.onRowSelect?.(item.pay_run_code)}
                  style={{
                    cursor: props.onRowSelect ? "pointer" : undefined,
                    backgroundColor:
                      item.pay_run_code === selectedRowCode ? "#e6f7ff" : "transparent",
                    transition: "background-color 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e: any) => {
                    if (item.pay_run_code !== selectedRowCode) {
                      e.currentTarget.style.backgroundColor = "#eaf6ff";
                      setHoveredRowIndex(index);
                    }
                  }}
                  onMouseLeave={(e: any) => {
                    if (item.pay_run_code !== selectedRowCode) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      setHoveredRowIndex(null);
                    }
                  }}
                >
                  <Table.Cell style={getStickyStyle(0, false, hoveredRowIndex === index)}>
                    {item.send_to_payroll_date
                      ? moment(item.send_to_payroll_date).format("DD/MM/YYYY")
                      : "-"}
                  </Table.Cell>
                  <Table.Cell style={getStickyStyle(1, false, hoveredRowIndex === index)}>
                    {item.employee_code}
                  </Table.Cell>
                  <Table.Cell style={getStickyStyle(2, false, hoveredRowIndex === index)}>
                    {item.employee_full_name}
                  </Table.Cell>
                  <Table.Cell style={getStickyStyle(3, false, hoveredRowIndex === index)}>
                    {positionOptions.find(
                      (pos: any) => pos.value === item.current_employee_position
                    )?.text ?? "-"}
                  </Table.Cell>
                  <Table.Cell style={getStickyStyle(4, false, hoveredRowIndex === index)}>
                    {item.employee_department}
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "salary" });
                      setEditValue(item.salary);
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "salary" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "salary")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "salary");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : (
                      formatNumber(item.salary)
                    )}
                  </Table.Cell>
                  <Table.Cell style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "10px",
                        minWidth: "130px",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <span
                        onClick={() => {
                          if (!canEdit) {
                            return;
                          }
                          setSelectedEmployee(item);
                          openModalForEmployee(item);
                        }}
                        style={{
                          flex: 1,
                          textAlign: "right",
                          cursor: canEdit ? "pointer" : "default",
                        }}
                      >
                        {formatNumber(item.overtime)}
                      </span>
                      {canEdit && (
                        <Icon
                          name="edit"
                          color="orange"
                          size="large"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedEmployee(item);
                            openModalForEmployee(item);
                          }}
                        />
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "position_pay" });
                      setEditValue(
                        Number(item.position_pay || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      );
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "position_pay" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => {
                          const numericValue = parseFloat(editValue.replace(/,/g, ""));
                          saveEdit(
                            { ...item, position_pay: isNaN(numericValue) ? 0 : numericValue },
                            "position_pay"
                          );
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const numericValue = parseFloat(editValue.replace(/,/g, ""));
                            saveEdit(
                              { ...item, position_pay: isNaN(numericValue) ? 0 : numericValue },
                              "position_pay"
                            );
                            setEditingCell(null);
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.position_pay ? (
                      Number(item.position_pay).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    ) : (
                      "0.00"
                    )}
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "license_allowance" });
                      setEditValue(
                        Number(item.license_allowance || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      );
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "license_allowance" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => {
                          const numericValue = parseFloat(editValue.replace(/,/g, ""));
                          saveEdit(
                            { ...item, license_allowance: isNaN(numericValue) ? 0 : numericValue },
                            "license_allowance"
                          );
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const numericValue = parseFloat(editValue.replace(/,/g, ""));
                            saveEdit(
                              {
                                ...item,
                                license_allowance: isNaN(numericValue) ? 0 : numericValue,
                              },
                              "license_allowance"
                            );
                            setEditingCell(null);
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.license_allowance ? (
                      Number(item.license_allowance).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    ) : (
                      "0.00"
                    )}
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "language_pay" });
                      setEditValue(
                        Number(item.language_pay || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      );
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "language_pay" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => {
                          const numericValue = parseFloat(editValue.replace(/,/g, ""));
                          saveEdit(
                            { ...item, language_pay: isNaN(numericValue) ? 0 : numericValue },
                            "language_pay"
                          );
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const numericValue = parseFloat(editValue.replace(/,/g, ""));
                            saveEdit(
                              { ...item, language_pay: isNaN(numericValue) ? 0 : numericValue },
                              "language_pay"
                            );
                            setEditingCell(null);
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.language_pay ? (
                      Number(item.language_pay).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    ) : (
                      "0.00"
                    )}
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "skill_pay" });
                      setEditValue(
                        Number(item.skill_pay || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      );
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "skill_pay" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => {
                          const numericValue = parseFloat(editValue.replace(/,/g, ""));
                          saveEdit(
                            { ...item, skill_pay: isNaN(numericValue) ? 0 : numericValue },
                            "skill_pay"
                          );
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const numericValue = parseFloat(editValue.replace(/,/g, ""));
                            saveEdit(
                              { ...item, skill_pay: isNaN(numericValue) ? 0 : numericValue },
                              "skill_pay"
                            );
                            setEditingCell(null);
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.skill_pay ? (
                      Number(item.skill_pay).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    ) : (
                      "0.00"
                    )}
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "bonus" });
                      setEditValue(item.bonus);
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "bonus" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "bonus")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "bonus");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : (
                      item.bonus
                    )}
                  </Table.Cell>

                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "doctor_fee" });
                      setEditValue(item.doctor_fee);
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "doctor_fee" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "doctor_fee")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "doctor_fee");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : (
                      item.doctor_fee
                    )}
                  </Table.Cell>
                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "providence_fund" });
                      setEditValue(item.providence_fund);
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "providence_fund" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "providence_fund")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "providence_fund");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.providence_fund !== "0.00" ? (
                      `-${item.providence_fund}`
                    ) : (
                      item.providence_fund
                    )}
                  </Table.Cell>

                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "absence" });
                      setEditValue(item.absence);
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "absence" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "absence")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "absence");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.absence !== "0.00" ? (
                      `-${item.absence}`
                    ) : (
                      item.absence
                    )}
                  </Table.Cell>

                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "with_holding_tax" });
                      setEditValue(item.with_holding_tax);
                    }}
                  >
                    {editingCell?.id === item.id && editingCell?.field === "with_holding_tax" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "with_holding_tax")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "with_holding_tax");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.with_holding_tax !== "0.00" ? (
                      `-${item.with_holding_tax}`
                    ) : (
                      item.with_holding_tax
                    )}
                  </Table.Cell>

                  <Table.Cell
                    style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                    onClick={() => {
                      if (!canEdit) {
                        return;
                      }
                      setEditingCell({ id: item.id, field: "social_security_fund" });
                      setEditValue(item.social_security_fund);
                    }}
                  >
                    {editingCell?.id === item.id &&
                    editingCell?.field === "social_security_fund" ? (
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveEdit(item, "social_security_fund")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveEdit(item, "social_security_fund");
                          }
                          if (e.key === "Escape") {
                            setEditingCell(null);
                          }
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    ) : item.social_security_fund !== "0.00" ? (
                      `-${item.social_security_fund}`
                    ) : (
                      item.social_security_fund
                    )}
                  </Table.Cell>
                  {dynamicPayrollItems.map((payItem: any, idx: number) => {
                    const found = item?.payroll_items?.find(
                      (pi: any) => pi.name_en === payItem.name_en
                    );
                    const amount = found?.amount ?? 0;
                    const isDeduct = payItem.type === "deduct";
                    const fieldKey = `payroll_item_${payItem.name_en}`;
                    return (
                      <Table.Cell
                        key={`payroll-cell-${item.id}-${idx}`}
                        style={canEdit ? { ...cellStyle, ...editableCellStyle } : cellStyle}
                        onClick={() => {
                          if (!canEdit) {
                            return;
                          }
                          setEditingCell({ id: item.id, field: fieldKey });
                          setEditValue(String(amount));
                        }}
                      >
                        {editingCell?.id === item.id && editingCell?.field === fieldKey ? (
                          <input
                            type="text"
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => saveEdit(item, fieldKey, payItem)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                saveEdit(item, fieldKey, payItem);
                              }
                              if (e.key === "Escape") {
                                setEditingCell(null);
                              }
                            }}
                            style={{ width: "100%", textAlign: "right" }}
                          />
                        ) : amount !== 0 ? (
                          isDeduct ? (
                            `-${formatNumber(amount)}`
                          ) : (
                            formatNumber(amount)
                          )
                        ) : (
                          "0.00"
                        )}
                      </Table.Cell>
                    );
                  })}
                  <Table.Cell style={cellStyle}>{item.total_revenue}</Table.Cell>
                  <Table.Cell style={cellStyle}>
                    {parseFloat(item.total_deduct) > 0
                      ? `-${item.total_deduct}`
                      : item.total_deduct}
                  </Table.Cell>
                  <Table.Cell style={cellStyle}>{item.net_salary}</Table.Cell>
                  {paySlip ? (
                    <Table.Cell style={{ ...cellStyle, textAlign: "center" }}>
                      <label
                        onClick={
                          props.buttonLoadCheck?.[`${item.id}_TH`] !== "LOADING"
                            ? () => handleGeneratePdfInThai(item.id)
                            : undefined
                        }
                        style={{
                          cursor:
                            props.buttonLoadCheck?.[`${item.id}_TH`] === "LOADING"
                              ? "default"
                              : "pointer",
                          color:
                            props.buttonLoadCheck?.[`${item.id}_TH`] === "LOADING"
                              ? "gray"
                              : "blue",
                          textDecoration:
                            props.buttonLoadCheck?.[`${item.id}_TH`] === "LOADING"
                              ? "none"
                              : "underline",
                          pointerEvents:
                            props.buttonLoadCheck?.[`${item.id}_TH`] === "LOADING"
                              ? "none"
                              : "auto",
                        }}
                      >
                        {formatPaySlipFileName(selectedSalaryPayRunDueDate) || "Pay Slip"}
                      </label>
                    </Table.Cell>
                  ) : null}
                </Table.Row>
              )
            )}
            <Table.Row
              style={{
                backgroundColor: "#f4f4f4",
                fontWeight: "bold",
                textAlign: "right",
              }}
            >
              <Table.Cell
                colSpan={5}
                textAlign="center"
                style={{
                  ...getStickyStyle(0),
                  backgroundColor: "#f4f4f4",
                  width: totalWidthPx,
                  minWidth: totalWidthPx,
                  maxWidth: totalWidthPx,
                }}
              >
                Total:
              </Table.Cell>
              <Table.Cell>{summary?.total_salary || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_overtime || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_position_pay || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_license_allowance || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_language_pay || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_skill_pay || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_bonus || "0.00"}</Table.Cell>
              <Table.Cell>{summary?.total_doctor_fee || "0.00"}</Table.Cell>
              <Table.Cell style={cellStyle}>
                {(() => {
                  const val = summary?.total_providence_fund || "0.00";
                  return val !== "0.00" ? `-${val}` : val;
                })()}
              </Table.Cell>
              <Table.Cell style={cellStyle}>
                {(() => {
                  const val = summary?.total_absence || "0.00";
                  return val !== "0.00" ? `-${val}` : val;
                })()}
              </Table.Cell>

              <Table.Cell style={cellStyle}>
                {(() => {
                  const val = summary?.total_with_holding_tax || "0.00";
                  return val !== "0.00" ? `-${val}` : val;
                })()}
              </Table.Cell>

              <Table.Cell style={cellStyle}>
                {(() => {
                  const val = summary?.total_social_security_fund || "0.00";
                  return val !== "0.00" ? `-${val}` : val;
                })()}
              </Table.Cell>
              {dynamicPayrollItems.map((item: any) => {
                const { name_en, type } = item;
                const snakeCase = (str: any) =>
                  str
                    .toLowerCase()
                    .replace(/\s+/g, "_")
                    .replace(/[^\w_]/g, "");
                const snakeKey = snakeCase(name_en);
                const totalKey =
                  type.toUpperCase() === "REVENUE"
                    ? `total_revenue_${snakeKey}`
                    : `total_deduct_${snakeKey}`;
                let value = summary[totalKey] ?? "0.00";
                if (type.toUpperCase() === "DEDUCT") {
                  value = `-${value}`;
                }
                return (
                  <Table.Cell key={snakeKey} textAlign="right">
                    {value}
                  </Table.Cell>
                );
              })}
              <Table.Cell>{summary?.total_revenue || "0.00"}</Table.Cell>
              <Table.Cell>
                {summary?.total_deduct && summary.total_deduct !== "0.00"
                  ? `-${summary.total_deduct}`
                  : summary?.total_deduct || "0.00"}
              </Table.Cell>
              <Table.Cell>{summary?.total_net_salary || "0.00"}</Table.Cell>
              {paySlip ? (
                <Table.Cell style={{ ...cellStyle, textAlign: "center" }}></Table.Cell>
              ) : null}
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  };

  useEffect(() => {
    const resetColumnWidths = () => {
      allColumnWidthsRef.current = {};
      setRenderTrigger((prev) => prev + 1);
    };

    resetColumnWidths();
  }, [activeSalaryTab, activeEditSalaryTab]);

  const handleGeneratePdfInThai = async (employeeId: string) => {
    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_PAY_SLIP_FILE",
      employeePayroll: employeeId,
      language: "TH",
    });
  };

  const handleGeneratePdfInEng = async (employeeId: string) => {
    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_PAY_SLIP_FILE",
      employeePayroll: employeeId,
      language: "EN",
    });
  };

  const [selectedPayRun, setSelectedPayRun] = useState<any | null>(null);
  const [employerSocialSec, setEmployerSocialSec] = useState("0.00");

  useEffect(() => {
    const summaryValue = props.PayrollSequence?.payrollSummaryParams?.total_social_security_fund;
    if (summaryValue !== undefined) {
      const parsed = parseFloat((summaryValue || "0").toString().replace(/,/g, ""));
      setEmployerSocialSec(parsed.toFixed(2));
    }
  }, [props.PayrollSequence?.payrollSummaryParams?.total_social_security_fund]);

  useEffect(() => {
    if (!selectedPayRun) {
      setEmployerSocialSec((750 * CreateSalaryData.length).toString());
    } else {
      const payRunMeta = groupedPayRuns.find((p) => p.code === selectedPayRun);
      if (payRunMeta?.employerSocialSec !== undefined) {
        setEmployerSocialSec(payRunMeta.employerSocialSec.toString());
      } else {
        setEmployerSocialSec("0.00");
      }
    }
  }, [selectedPayRun]);

  const [filteredAndSearchedData, setFilteredAndSearchedData] = useState<any[]>([]);

  console.log("filtered data" + filteredAndSearchedData);

  const renderSalaryTab = () => {
    if (activeSalaryTab === "CREATESALARY") {
      return renderCreateSalary(undefined, employerSocialSec, setEmployerSocialSec, version);
    }
    return renderSalaryPayRun();
  };

  function generatePayRunCode(): string {
    const now = new Date();

    const year = now.getFullYear() % 100;
    const YY = year.toString().padStart(2, "0");
    const MM = (now.getMonth() + 1).toString().padStart(2, "0");
    const DD = now.getDate().toString().padStart(2, "0");
    const HH = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");
    const ss = now.getSeconds().toString().padStart(2, "0");

    return `MSK-${YY}${MM}${DD}${HH}${mm}${ss}`;
  }

  const [generatedPayrunCode, setGeneratedPayrunCode] = React.useState(generatePayRunCode());

  function getTodayString() {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, "0");
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function convertDMYtoYMD(dmy: string): string {
    if (!dmy || typeof dmy !== "string") {
      return "";
    }
    const parts = dmy.split("/");
    if (parts.length !== 3) {
      return "";
    }
    const [dd, mm, yyyyRaw] = parts;
    if (!dd || !mm || !yyyyRaw) {
      return "";
    }
    const yyyy = parseInt(yyyyRaw, 10);
    const gregorianYear = yyyy > 2400 ? yyyy - 543 : yyyy;
    return `${gregorianYear}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }

  const [payRunCode, setPayRunCode] = useState<string>("");

  const [salaryIssuedDate, setSalaryIssuedDate] = React.useState(getTodayString());
  const [salaryDueDate, setSalaryDueDate] = React.useState(getTodayString());
  const [isDueDateComputed, setIsDueDateComputed] = useState(false);
  const [hasSearchedCreate, setHasSearchedCreate] = useState(false);
  const [hasSearchedEdit, setHasSearchedEdit] = useState(false);
  const [openCreateSalaryModal, setOpenCreateSalaryModal] = useState(false);
  const selectedSummary = props.PayrollSequence?.searchResultSummary;
  const [currentStatus, setCurrentStatus] = useState(
    selectedSummary?.status?.toUpperCase() ?? "DRAFT"
  );

  const [createIssuedDate, setCreateIssuedDate] = useState<string | null>(null);
  const [createDueDate, setCreateDueDate] = useState<string | null>(null);

  const [editIssuedDate, setEditIssuedDate] = useState("");
  const [editDueDate, setEditDueDate] = useState("");

  const [ssoDueDate, setSsoDueDate] = useState("");

  const salaryPayRunId = props?.PayrollSequence?.salaryPayRun ?? null;
  const payRunData = props.PayrollSequence?.salaryPayRunList
    ? props.PayrollSequence.salaryPayRunList.filter((item: any) => item.id === salaryPayRunId)
    : [];
  const selectedRunData = payRunData?.[0] ?? {};

  const [searchCriteria, setSearchCriteria] = useState({
    startDate: "",
    endDate: "",
    department: "",
    position: "",
    salaryPayRun: "",
  });

  const [showRecordConfirmModal, setShowRecordConfirmModal] = useState(false);

  useEffect(() => {
    if (activeSalaryTab === "SALARYPAYRUN" && selectedRunData) {
      setEditIssuedDate(selectedRunData.issued_date ?? "");
      setEditDueDate(selectedRunData.due_date ?? "");
    }
  }, [activeSalaryTab, selectedRunData, salaryIssuedDate, salaryDueDate]);

  useEffect(() => {
    if (activeSalaryTab === "SALARYPAYRUN" && selectedRunData) {
      setSsoDueDate(selectedRunData.due_date ?? "");
    } else {
      setSsoDueDate("");
    }
  }, [activeSalaryTab, selectedRunData]);

  useEffect(() => {
    if (
      activeSalaryTab === "CREATESALARY" &&
      !hasSearchedCreate &&
      !payRunCode &&
      searchCriteria.startDate &&
      searchCriteria.endDate
    ) {
      fetchEmployeePayrollList({}, { value: searchCriteria });
      setHasSearchedCreate(true);
    }
  }, [activeSalaryTab, hasSearchedCreate, payRunCode, searchCriteria]);

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [lastCancelledPayRunCode, setLastCancelledPayRunCode] = useState<string>("");
  const [lastSubmittedPayRun, setLastSubmittedPayRun] = useState<any>(null);

  function formatToADDate(date?: string | Date | null): string {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB");
  }

  const [currentLoadingButton, setCurrentLoadingButton] = useState<"DRAFT" | "WAIT_APPROVE" | null>(
    null
  );

  const [currentLoadingStatusButton, setCurrentLoadingStatusButton] = useState<
    | "SAVE_DRAFT"
    | "SEND_FOR_APPROVE"
    | "CANCEL_WAIT_APPROVE"
    | "APPROVE_WAIT_APPROVE"
    | "CANCEL_WAIT_FINAL_APPROVE"
    | "CANCEL_FINAL_APPROVE"
    | "FINAL_APPROVE"
    | null
  >(null);

  const [successButton, setSuccessButton] = useState<"DRAFT" | "WAIT_APPROVE" | null>(null);
  const [successStatusButton, setSuccessStatusButton] =
    useState<typeof currentLoadingStatusButton>(null);
  const triggerAction = (
    key: typeof currentLoadingStatusButton,
    action: () => Promise<void> | void
  ) => {
    if (currentLoadingStatusButton) return;
    setCurrentLoadingStatusButton(key);

    Promise.resolve(action())
      .then(() => {
        setSuccessStatusButton(key);
        setTimeout(() => setSuccessStatusButton(null), 2000);
      })
      .catch(console.error)
      .finally(() => {
        setTimeout(() => setCurrentLoadingStatusButton(null), 800);
      });
  };

  const [openCancelReasonModal, setOpenCancelReasonModal] = useState(false);
  const [pendingCancelRun, setPendingCancelRun] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const [openRevertReasonModal, setOpenRevertReasonModal] = useState(false);
  const [pendingRevertRun, setPendingRevertRun] = useState<any>(null);
  const [revertReason, setRevertReason] = useState<string[]>([]);
  const [isReverting, setIsReverting] = useState(false);
  const [pendingRevertActionKey, setPendingRevertActionKey] = useState<
    "CANCEL_WAIT_APPROVE" | "CANCEL_WAIT_FINAL_APPROVE" | "CANCEL_FINAL_APPROVE" | null
  >(null);

  const renderCreateSalary = (
    payRunCode?: string,
    employerSocialSec?: string,
    setEmployerSocialSec?: (val: string) => void,
    version?: number
  ) => {
    if (activeSalaryTab !== "CREATESALARY" && !payRunCode) {
      return null;
    }

    const employeeList =
      activeSalaryTab === "CREATESALARY"
        ? hasSearchedCreate
          ? props.PayrollSequence?.employeePayrollList ?? []
          : []
        : props.PayrollSequence?.employeePayrollList ?? [];

    const employeePayrollIds =
      activeSalaryTab === "SALARYPAYRUN" && selectedRun?.id
        ? employeeList
            .filter((e: any) => `${e.salary_pay_run}` === `${selectedRun.id}`)
            .map((e: any) => e.id)
        : employeeList.map((e: any) => e.id);
    const summaryList = props.PayrollSequence?.payrollSummaryParams ?? {};
    const salaryPayRunId = props?.PayrollSequence?.salaryPayRun ?? null;
    console.log("salary pay run id", salaryPayRunId);

    const canEdit =
      activeSalaryTab === "CREATESALARY" ||
      selectedStatus === "DRAFT" ||
      selectedStatus === "WAIT_APPROVE";

    const isCreateTab = activeSalaryTab === "CREATESALARY";
    const selectedSalaryPayRun = props.PayrollSequence?.salaryPayRunList
      ? props.PayrollSequence.salaryPayRunList.filter((item: any) => item.id === salaryPayRunId)
      : [];
    const employeesForPayRun = props.PayrollSequence?.searchResultEmployeePayrollList || [];
    const summaryForPayRun = props.PayrollSequence?.searchResultSummary || [];
    console.log("employee list for pay run", employeeList);
    console.log("employee for pay run", employeesForPayRun);
    console.log("summary for pay run", summaryForPayRun);
    const salarySummary =
      isCreateTab && !hasSearchedCreate ? {} : isCreateTab ? summaryList : summaryForPayRun;

    const employerValueNumber =
      isCreateTab && !hasSearchedCreate ? 0 : parseFloat(employerSocialSec || "0") || 0;
    const selectedRunData = selectedSalaryPayRun?.[0] ?? {};

    const issuedDateToUse = isCreateTab ? createIssuedDate || salaryIssuedDate : editIssuedDate;

    const dueDateToUse = isCreateTab ? createDueDate || salaryDueDate : editDueDate;
    const positionOptions = [
      { key: "all", text: "All", value: "" },
      ...(props.masterOptions?.positionAll || []),
    ];
    const positionText =
      positionOptions.find((opt) => opt.value === searchCriteria.position)?.text || "";
    const departmentOptions = [
      { key: "all", text: "All", value: "" },
      ...(props.masterOptions?.division || []),
    ];
    const departmentText =
      positionOptions.find((opt) => opt.value === searchCriteria.department)?.text || "";

    const employeeListForTable =
      activeSalaryTab === "CREATESALARY"
        ? hasSearchedCreate
          ? employeeList ?? []
          : []
        : activeEditSalaryTab === "EDIT_SALARY"
        ? hasSearchedEdit
          ? employeeList ?? []
          : employeesForPayRun ?? []
        : [];

    const summaryForTable =
      activeSalaryTab === "CREATESALARY"
        ? hasSearchedCreate
          ? summaryList ?? []
          : []
        : activeEditSalaryTab === "EDIT_SALARY"
        ? hasSearchedEdit
          ? summaryList ?? []
          : summaryForPayRun ?? []
        : [];

    const rawEmployeeSSF = summaryForTable?.total_social_security_fund || "0.00";

    const employerValue =
      canEdit && employerSocialSec !== undefined
        ? parseFloat(employerSocialSec || rawEmployeeSSF.replace(/,/g, "") || "0")
        : parseFloat(selectedSalaryPayRun?.[0]?.social_security_employer || "0");

    const employeeSocialSec = parseFloat(rawEmployeeSSF.replace(/,/g, ""));
    const payableSocialSec = employerValueNumber + Math.abs(employeeSocialSec);

    const basePayRunData = () => ({
      id: selectedRun?.id,
      pay_run_code: selectedRun?.pay_run_code,
      issued_date: convertDMYtoYMD(issuedDateToUse),
      due_date: convertDMYtoYMD(dueDateToUse),
      payment_date: null,
      cycle_date_start: convertDMYtoYMD(selectedRun.cycle_date_start),
      cycle_date_end: convertDMYtoYMD(selectedRun.cycle_date_end),
      social_security_employer: parseFloat(employerSocialSec || "0").toFixed(2),
      employee_payrolls: employeePayrollIds,
    });

    const handleSubmitSalaryPayRun = (statusToUse?: string) => {
      const data = {
        pay_run_code: generatedPayrunCode,
        issued_date: convertDMYtoYMD(issuedDateToUse),
        due_date: convertDMYtoYMD(dueDateToUse),
        payment_date: null,
        cycle_date_start: convertDMYtoYMD(searchCriteria.startDate),
        cycle_date_end: convertDMYtoYMD(searchCriteria.endDate),
        social_security_employer: parseFloat(employerSocialSec || "").toFixed(2),
        status: statusToUse || currentStatus,
        employee_payrolls: employeePayrollIds,
      };

      console.log("Submitting salary pay run data:", data);

      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SUBMIT_SALARY_PAY_RUN",
        salaryPayRun: data,
      });

      setHasSearchedCreate(false);
    };

    return (
      <div style={{ padding: "20px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", whiteSpace: "nowrap" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                paddingRight: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>From Date</label>

              <DateTextBox
                placeholder="dd/mm/yyyy"
                value={searchCriteria.startDate}
                inputStyle={{ width: "140px" }}
                onChange={(dateStr: string) =>
                  setSearchCriteria((prev) => ({
                    ...prev,
                    startDate: dateStr && dateStr.includes("/") ? parseBEDateInput(dateStr) : "",
                  }))
                }
              />
            </div>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                paddingRight: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>To Date</label>

              <DateTextBox
                placeholder="dd/mm/yyyy"
                value={searchCriteria.endDate}
                inputStyle={{ width: "140px" }}
                onChange={(dateStr: string) =>
                  setSearchCriteria((prev) => ({
                    ...prev,
                    endDate: dateStr && dateStr.includes("/") ? parseBEDateInput(dateStr) : "",
                  }))
                }
              />
            </div>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                paddingRight: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Department</label>

              <Dropdown
                selection
                search
                style={{ width: "140px" }}
                options={departmentOptions}
                value={searchCriteria.department}
                onChange={(_e, { value }) =>
                  setSearchCriteria((prev) => ({ ...prev, department: value as string }))
                }
                text={departmentText}
              />
            </div>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                paddingRight: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Position</label>

              <Dropdown
                selection
                search
                style={{ width: "140px" }}
                options={positionOptions}
                value={searchCriteria.position}
                onChange={(_e, { value }) =>
                  setSearchCriteria((prev) => ({ ...prev, position: value as string }))
                }
                text={positionText}
              />
            </div>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ButtonLoadCheck
                color="blue"
                onClick={(e) => {
                  if (activeSalaryTab === "CREATESALARY") {
                    fetchEmployeePayrollList(e, { value: searchCriteria });
                  } else if (
                    activeSalaryTab === "SALARYPAYRUN" &&
                    activeEditSalaryTab === "EDIT_SALARY"
                  ) {
                    fetchEmployeePayrollList(e, {
                      value: searchCriteria,
                      salaryPayRun: selectedRun?.id,
                    });
                  }
                }}
                setProp={props.setProp}
                fluid={false}
                size="medium"
                paramKey={BTN_ACTS.FETCH_EMPLOYEE_PAYROLL_LIST}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.FETCH_EMPLOYEE_PAYROLL_LIST]}
                title="Search"
              />
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Issued Date</label>
              <DateTextBox
                placeholder="dd/mm/yyyy"
                value={issuedDateToUse}
                onChange={(val: string) => {
                  if (!val) {
                    if (isCreateTab) {
                      setCreateIssuedDate(null);
                    } else {
                      setEditIssuedDate("");
                    }
                  } else {
                    const adDate = parseBEDateInput(val);
                    if (isCreateTab) {
                      setCreateIssuedDate(adDate);
                    } else {
                      setEditIssuedDate(adDate);
                    }
                  }
                }}
                inputStyle={{
                  width: "140px",
                  backgroundColor: canEdit ? "#fff" : "#f0f0f0",
                  cursor: canEdit ? "text" : "not-allowed",
                }}
                disabled={!canEdit}
              />
            </div>
            <div
              style={{
                minWidth: "150px",
                paddingBottom: "5px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Due Date</label>
              <DateTextBox
                placeholder="dd/mm/yyyy"
                value={dueDateToUse}
                onChange={(val: string) => {
                  if (!val) {
                    if (isCreateTab) {
                      setCreateDueDate(null);
                    } else {
                      setEditDueDate("");
                    }
                  } else {
                    const adDate = parseBEDateInput(val);
                    if (isCreateTab) {
                      setCreateDueDate(adDate);
                    } else {
                      setEditDueDate(adDate);
                    }
                  }
                }}
                inputStyle={{
                  width: "140px",
                  backgroundColor: canEdit ? "#fff" : "#f0f0f0",
                  cursor: canEdit ? "text" : "not-allowed",
                }}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            paddingBottom: "20px",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              backgroundColor: "var(--light-blue-label-theme-color)",
              borderRadius: "6px",
              padding: "5px 15px",
              width: "300px",
              height: "36px",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Employees List
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "8px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>
                Pay Run Code<span style={{ color: "red" }}> *</span>
              </label>
              <Input
                style={{
                  width: "300px",
                  height: "36px",
                  backgroundColor: isCreateTab ? "#fff" : "#f0f0f0",
                  cursor: isCreateTab ? "text" : "not-allowed",
                }}
                value={isCreateTab ? generatedPayrunCode : selectedPayRun}
                readOnly={!canEdit}
              />
            </div>
          </div>
        </div>
        <div>{salaryItemsTable(employeeListForTable, currentStatus, summaryForTable)}</div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            padding: "10px 0",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              backgroundColor: "var(--light-blue-label-theme-color)",
              borderRadius: "6px",
              padding: "5px 15px",
              width: "300px",
              height: "36px",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Salary pay run summary
          </label>
        </div>
        <div
          style={{
            marginTop: "10px",
            border: "1px solid #ccc",
            padding: "15px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
          }}
        >
          <div style={{ width: "50%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "stretch",
                padding: "10px 0",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "var(--light-blue-label-theme-color)",
                  borderRadius: "6px",
                  padding: "5px 15px",
                  textAlign: "left",
                  height: "70px",
                  fontWeight: "bold",
                  fontSize: "17px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Net amount to pay</span>
                <span>{formatNumber(summaryForTable?.total_net_salary || "0.00")} Baht</span>
              </div>
            </div>
            {[
              { label: "Total Salary", value: summaryForTable?.total_salary },
              {
                label: "Add Summary of revenue items",
                value: summaryForTable?.non_salary_revenue,
                color: "green",
              },
              {
                label: "Less Summary of deduction items",
                value: summaryForTable?.non_social_security_deduct,
                color: "red",
                parentheses: true,
              },
              {
                label: "Less Social Security",
                value: formatNumber(employeeSocialSec),
                color: "red",
              },
              {
                label: "Less Withholding Tax",
                value: summaryForTable?.total_with_holding_tax,
                color: "red",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  width: "100%",
                  fontWeight: "bold",
                  paddingBottom: "10px",
                  margin: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: item.color || "inherit",
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    backgroundColor: "#ececec",
                    borderRadius: "6px",
                    textAlign: "right",
                    padding: "5px",
                    minWidth: "250px",
                    color: "black",
                  }}
                >
                  {(() => {
                    const val = item.value || "0.00";
                    if (item.parentheses) {
                      return val !== "0.00" ? `(${val})` : "(0.00)";
                    }
                    return val !== "0.00" ? val : "0.00";
                  })()}{" "}
                  Baht
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              width: "50%",
              paddingLeft: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ marginTop: "auto", padding: "15px", fontWeight: "bold" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "10px",
                }}
              >
                <span style={{ color: "black" }}>Social Security (Employer)</span>

                {canEdit ? (
                  <Input
                    type="number"
                    value={employerSocialSec}
                    onChange={(e, data) => {
                      if (setEmployerSocialSec) {
                        setEmployerSocialSec(data.value);
                      }
                    }}
                    onBlur={() => {
                      if (setEmployerSocialSec) {
                        const val = parseFloat(employerSocialSec || "0");
                        setEmployerSocialSec(val.toFixed(2));
                      }
                    }}
                    style={{ width: "250px", fontWeight: "bold" }}
                    input={{
                      style: {
                        backgroundColor: "#fff",
                        textAlign: "right",
                      },
                    }}
                  />
                ) : (
                  <span
                    style={{
                      width: "250px",
                      fontWeight: "bold",
                      textAlign: "right",
                      backgroundColor: "#f0f0f0",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      display: "inline-block",
                    }}
                  >
                    {formatNumber(employerValue)} Baht
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "10px",
                }}
              >
                <span style={{ color: "red" }}>Payable Social Security</span>
                <span
                  style={{
                    backgroundColor: "#ececec",
                    borderRadius: "6px",
                    textAlign: "right",
                    padding: "5px",
                    minWidth: "250px",
                    color: "black",
                  }}
                >
                  {formatNumber(payableSocialSec)} Baht
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "red" }}>Payable Withholding Tax</span>
                <span
                  style={{
                    backgroundColor: "#ececec",
                    borderRadius: "6px",
                    textAlign: "right",
                    padding: "5px",
                    minWidth: "250px",
                    color: "black",
                  }}
                >
                  {formatNumber(
                    parseFloat(
                      (summaryForTable?.total_with_holding_tax || "0").replace(/,/g, "")
                    ) || 0
                  )}{" "}
                  Baht
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}
        >
          {(() => {
            const buttons = [];
            const isCreateTab = activeSalaryTab === "CREATESALARY";
            const isPayRunTab = activeSalaryTab === "SALARYPAYRUN";
            const upperStatus = currentStatus?.toUpperCase() ?? "DRAFT";

            if (isCreateTab) {
              buttons.push(
                <ButtonLoadCheck
                  key="saveDraft"
                  color="orange"
                  onClick={() => {
                    if (currentLoadingButton) return;
                    setCurrentLoadingButton("DRAFT");
                    setCurrentStatus("DRAFT");
                    handleSubmitSalaryPayRun("DRAFT");
                    setHasSearchedCreate(false);
                  }}
                  setProp={props.setProp}
                  fluid={false}
                  size="medium"
                  paramKey={BTN_ACTS.HANDLE_SUBMIT_SALARY_PAY_RUN}
                  buttonLoadCheck={
                    currentLoadingButton === "DRAFT"
                      ? "LOADING"
                      : successButton === "DRAFT"
                      ? "SUCCESS"
                      : undefined
                  }
                  title="Save Draft"
                />,
                <ButtonLoadCheck
                  key="sendForApprove"
                  color="green"
                  onClick={() => {
                    if (currentLoadingButton) return;
                    setCurrentLoadingButton("WAIT_APPROVE");
                    setCurrentStatus("WAIT_APPROVE");
                    handleSubmitSalaryPayRun("WAIT_APPROVE");
                    resetCreateSalaryState();
                    setHasSearchedCreate(false);
                  }}
                  setProp={props.setProp}
                  fluid={false}
                  size="medium"
                  paramKey={BTN_ACTS.HANDLE_SUBMIT_SALARY_PAY_RUN}
                  buttonLoadCheck={
                    currentLoadingButton === "WAIT_APPROVE"
                      ? "LOADING"
                      : successButton === "WAIT_APPROVE"
                      ? "SUCCESS"
                      : undefined
                  }
                  title="Send for Approve"
                />
              );
            }

            if (isPayRunTab) {
              if (selectedStatus === "DRAFT") {
                buttons.push(
                  <ButtonLoadCheck
                    key="cancel"
                    color="red"
                    onClick={() => {
                      setPendingCancelRun(selectedRun);
                      setCancelReason([]);
                      setOpenCancelReasonModal(true);
                    }}
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_DELETE_SALARY_PAY_RUN}
                    buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_DELETE_SALARY_PAY_RUN]}
                    title="Cancel"
                  />,
                  <ButtonLoadCheck
                    color="orange"
                    onClick={() =>
                      triggerAction("SAVE_DRAFT", () => {
                        const updatedPayRun = { ...basePayRunData(), status: "DRAFT" };
                        return handleEditSalaryPayRun(null, { value: null }, updatedPayRun);
                      })
                    }
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "SAVE_DRAFT"
                        ? "LOADING"
                        : successStatusButton === "SAVE_DRAFT"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Save Draft"
                  />,
                  <ButtonLoadCheck
                    color="green"
                    onClick={() =>
                      triggerAction("SEND_FOR_APPROVE", () => {
                        const updatedPayRun = { ...basePayRunData(), status: "WAIT_APPROVE" };
                        return handleEditSalaryPayRun(null, { value: null }, updatedPayRun);
                      })
                    }
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "SEND_FOR_APPROVE"
                        ? "LOADING"
                        : successStatusButton === "SEND_FOR_APPROVE"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Send for Approve"
                  />
                );
              } else if (selectedStatus === "WAIT_APPROVE") {
                buttons.push(
                  <ButtonLoadCheck
                    color="red"
                    onClick={() => handleOpenRevertModal(selectedRun, "CANCEL_WAIT_APPROVE")}
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "CANCEL_WAIT_APPROVE"
                        ? "LOADING"
                        : successStatusButton === "CANCEL_WAIT_APPROVE"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Cancel"
                  />,
                  <ButtonLoadCheck
                    color="green"
                    onClick={() =>
                      triggerAction("APPROVE_WAIT_APPROVE", () => {
                        const updatedPayRun = { ...basePayRunData(), status: "WAIT_FINAL_APPROVE" };
                        return handleEditSalaryPayRun(null, { value: null }, updatedPayRun);
                      })
                    }
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "APPROVE_WAIT_APPROVE"
                        ? "LOADING"
                        : successStatusButton === "APPROVE_WAIT_APPROVE"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Approve"
                  />
                );
              } else if (selectedStatus === "WAIT_FINAL_APPROVE") {
                buttons.push(
                  <ButtonLoadCheck
                    color="red"
                    onClick={() => handleOpenRevertModal(selectedRun, "CANCEL_WAIT_FINAL_APPROVE")}
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "CANCEL_WAIT_FINAL_APPROVE"
                        ? "LOADING"
                        : successStatusButton === "CANCEL_WAIT_FINAL_APPROVE"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Cancel"
                  />,
                  <ButtonLoadCheck
                    color="green"
                    onClick={() =>
                      triggerAction("FINAL_APPROVE", () => {
                        const updatedPayRun = { ...basePayRunData(), status: "OUTSTANDING" };
                        return handleEditSalaryPayRun(null, { value: null }, updatedPayRun);
                      })
                    }
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "FINAL_APPROVE"
                        ? "LOADING"
                        : successStatusButton === "FINAL_APPROVE"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Final Approve"
                  />
                );
              } else if (selectedStatus === "OUTSTANDING") {
                buttons.push(
                  <ButtonLoadCheck
                    color="red"
                    onClick={() => handleOpenRevertModal(selectedRun, "CANCEL_FINAL_APPROVE")}
                    setProp={props.setProp}
                    fluid={false}
                    size="medium"
                    paramKey={BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN}
                    buttonLoadCheck={
                      currentLoadingStatusButton === "CANCEL_FINAL_APPROVE"
                        ? "LOADING"
                        : successStatusButton === "CANCEL_FINAL_APPROVE"
                        ? "SUCCESS"
                        : undefined
                    }
                    title="Cancel Final Approve"
                  />
                );
              }
            }

            if (!isCreateTab || selectedStatus === "PAID") {
              buttons.push(
                <Button key="close" color="grey" onClick={() => setOpenCreateSalaryModal(false)}>
                  Close
                </Button>
              );
            }

            return buttons;
          })()}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_SALARY_PAY_RUN] === "SUCCESS") {
      const payRunList = props.PayrollSequence?.salaryPayRunList || [];
      const last = payRunList[payRunList.length - 1] || null;
      setLastSubmittedPayRun(last);
      setSuccessModalOpen(true);
    }
  }, [props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_SALARY_PAY_RUN]]);

  useEffect(() => {
    if (props.buttonLoadCheck?.[BTN_ACTS.HANDLE_DELETE_SALARY_PAY_RUN] === "SUCCESS") {
      setCancelModalOpen(true);
    }
  }, [props.buttonLoadCheck?.[BTN_ACTS.HANDLE_DELETE_SALARY_PAY_RUN], openCreateSalaryModal]);

  useEffect(() => {
    const status = props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_SALARY_PAY_RUN];
    if (status === "SUCCESS" && currentLoadingButton) {
      setSuccessButton(currentLoadingButton);
      const timer = setTimeout(() => {
        setCurrentLoadingButton(null);
        setSuccessButton(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [props.buttonLoadCheck, currentLoadingButton]);

  useEffect(() => {
    const status = props.buttonLoadCheck?.[BTN_ACTS.HANDLE_EDIT_SALARY_PAY_RUN];
    if (status === "SUCCESS" && currentLoadingStatusButton) {
      const timer = setTimeout(() => {
        setSuccessStatusButton(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [props.buttonLoadCheck, currentLoadingStatusButton]);

  const resetCreateSalaryState = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const formatDate = (date: Date) =>
      date
        .toLocaleDateString("en-GB")
        .split("/")
        .map((part) => part.padStart(2, "0"))
        .join("/");

    if (activeSalaryTab === "CREATESALARY") {
      setSearchCriteria({
        startDate: formatDate(firstDay),
        endDate: formatDate(lastDay),
        department: "",
        position: "",
        salaryPayRun: "",
      });
    } else if (activeSalaryTab === "SALARYPAYRUN") {
      setSearchCriteria({
        startDate: "",
        endDate: "",
        department: "",
        position: "",
        salaryPayRun: "",
      });
    }
    setHasSearchedCreate(false);
    setHasSearchedEdit(false);

    const summaryValue = props.PayrollSequence?.payrollSummaryParams?.total_social_security_fund;
    if (summaryValue !== undefined) {
      const parsed = parseFloat((summaryValue || "0").toString().replace(/,/g, ""));
      setEmployerSocialSec(parsed.toFixed(2));
    }
    setSelectedStatus("");
  };

  const resetEditSalaryState = () => {
    setSearchCriteria({
      startDate: "",
      endDate: "",
      department: "",
      position: "",
      salaryPayRun: "",
    });

    setHasSearchedCreate(false);
    setHasSearchedEdit(false);

    setSelectedStatus("");
    setEmployerSocialSec(selectedRun.social_security_employer);
    setEditIssuedDate(selectedRun.issued_date);
    setEditDueDate(selectedRun.due_date);
    setPayRunCode(selectedRun.pay_run_code);
  };

  const hasSetDefaultDateRef = useRef(false);

  useEffect(() => {
    if (activeSalaryTab === "CREATESALARY" && salaryDueDate && isDueDateComputed) {
      setSearchCriteria({
        startDate: "",
        endDate: "",
        department: "",
        position: "",
        salaryPayRun: "",
      });

      setHasSearchedCreate(false);
      const summaryValue = props.PayrollSequence?.payrollSummaryParams?.total_social_security_fund;
      if (summaryValue !== undefined) {
        const parsed = parseFloat((summaryValue || "0").toString().replace(/,/g, ""));
        setEmployerSocialSec(parsed.toFixed(2));
      }
      setSelectedStatus("");

      hasSetDefaultDateRef.current = false;
      setGeneratedPayrunCode(generatePayRunCode());
    }
  }, [activeSalaryTab, salaryDueDate]);

  useEffect(() => {
    if (
      activeSalaryTab === "CREATESALARY" &&
      !hasSetDefaultDateRef.current &&
      !searchCriteria.startDate &&
      !searchCriteria.endDate
    ) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const formatDate = (date: Date) =>
        date
          .toLocaleDateString("en-GB")
          .split("/")
          .map((part) => part.padStart(2, "0"))
          .join("/");

      setSearchCriteria((prev) => ({
        ...prev,
        startDate: formatDate(firstDay),
        endDate: formatDate(lastDay),
      }));
      setHasSearchedCreate(false);
      setCreateIssuedDate(salaryIssuedDate);
      setCreateDueDate(salaryDueDate);
      hasSetDefaultDateRef.current = true;
    }
  }, [searchCriteria.startDate, searchCriteria.endDate, activeSalaryTab]);

  useEffect(() => {
    if (activeSalaryTab === "CREATESALARY") {
      let criteria = {
        ...searchCriteria,
        salaryPayRun: "",
      };

      if (!criteria.startDate || !criteria.endDate) {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        const formatDate = (date: Date) =>
          date
            .toLocaleDateString("en-GB")
            .split("/")
            .map((part) => part.padStart(2, "0"))
            .join("/");

        criteria.startDate = formatDate(firstDay);
        criteria.endDate = formatDate(lastDay);
      }

      setSearchCriteria(criteria);

      fetchEmployeePayrollList(null, { value: criteria });
      setHasSearchedCreate(true);
    }
  }, [activeSalaryTab]);

  useEffect(() => {
    if (activeSalaryTab === "SALARYPAYRUN") {
      setSearchCriteria({
        startDate: "",
        endDate: "",
        department: "",
        position: "",
        salaryPayRun: "",
      });
      setHasSearchedEdit(false);
    }
  }, [activeSalaryTab]);

  useEffect(() => {
    if (activeEditSalaryTab === "EDIT_SALARY") {
      setSearchCriteria({
        startDate: "",
        endDate: "",
        department: "",
        position: "",
        salaryPayRun: "",
      });
    }
  }, [activeEditSalaryTab]);

  const selectedRun = salaryPayRunList.find((r: any) => r.pay_run_code === selectedPayRun);

  useEffect(() => {
    if (
      activeEditSalaryTab === "EDIT_SALARY" &&
      selectedRun?.id &&
      props.PayrollSequence?.salaryPayRunList
    ) {
      const match = props.PayrollSequence.salaryPayRunList.find(
        (item: any) => item.id === selectedRun.id
      );

      if (match?.social_security_employer !== undefined) {
        setEmployerSocialSec(parseFloat(match.social_security_employer).toFixed(2));
      }
    }
  }, [activeEditSalaryTab, selectedRun?.id, props.PayrollSequence?.salaryPayRunList]);

  useEffect(() => {
    if (
      activeSalaryTab === "SALARYPAYRUN" &&
      activeEditSalaryTab === "EDIT_SALARY" &&
      selectedRun?.id
    ) {
      setHasSearchedEdit(false);
      handleSearchEmployeePayroll(null, { salaryPayRun: selectedRun.id });
    }
  }, [activeSalaryTab, activeEditSalaryTab, selectedRun?.id]);

  const renderSalaryDetail = (
    payRunCode?: string,
    payRunId?: number,
    employerSocialSec?: string
  ) => {
    if (!payRunCode) {
      return null;
    }

    const summary = props.PayrollSequence?.searchResultSummary;
    const employees = props.PayrollSequence?.searchResultEmployeePayrollList;

    const selectedSalaryPayRun = props.PayrollSequence?.salaryPayRunList
      ? props.PayrollSequence.salaryPayRunList.filter((item: any) => item.id === payRunId)
      : [];
    console.log("selected salary pay run", selectedSalaryPayRun);
    console.log("selected salary pay run issued date", selectedSalaryPayRun?.[0]?.issued_date);

    const currentStatus = (selectedSalaryPayRun.status ?? "DRAFT").toUpperCase();

    const employerValue = parseFloat(selectedSalaryPayRun?.[0]?.social_security_employer || "0");
    const employeeSocialSec = parseFloat(
      (summary?.total_social_security_fund || "0").replace(/,/g, "")
    );
    const payableSocialSec = employerValue + Math.abs(employeeSocialSec);
    console.log("salary detail", employerValue, employeeSocialSec, payableSocialSec);

    const handleSalaryPayRunReport = () => {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SALARY_PAY_RUN_REPORT",
        salaryPayRun: selectedRun.id,
      });
    };

    return (
      <div style={{ padding: "20px 0" }}>
        <div style={{ paddingBottom: "20px", position: "relative" }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "30px",
              alignItems: "flex-start",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Issued Date</label>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "180px",
                }}
              >
                {selectedSalaryPayRun?.[0]?.issued_date}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Date Range</label>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "220px",
                }}
              >
                {selectedSalaryPayRun?.[0]?.cycle_date_start} -{" "}
                {selectedSalaryPayRun?.[0]?.cycle_date_end}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Due Date</label>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "180px",
                }}
              >
                {selectedSalaryPayRun?.[0]?.due_date}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Number of Employees</label>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "120px",
                  textAlign: "center",
                }}
              >
                {selectedSalaryPayRun?.[0]?.employee_amount}
              </div>
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              paddingTop: "10px",
              paddingRight: "10px",
            }}
          >
            <ButtonLoadCheck
              color="blue"
              onClick={handleSalaryPayRunReport}
              setProp={props.setProp}
              fluid={false}
              size="medium"
              icon="upload"
              paramKey={BTN_ACTS.HANDLE_SALARY_PAY_RUN_REPORT}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SALARY_PAY_RUN_REPORT]}
              title="Salary Pay Run Report"
            />
          </div>
        </div>

        <label
          style={{
            backgroundColor: "var(--light-blue-label-theme-color)",
            borderRadius: "6px",
            padding: "5px 15px",
            textAlign: "left",
            width: "300px",
            height: "36px",
            fontWeight: "bold",
            marginBottom: "15px",
            display: "inline-block",
          }}
        >
          Employees List
        </label>
        <div>
          {salaryItemsTable(employees, currentStatus, summary, selectedSalaryPayRun?.[0].due_date)}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            padding: "10px 0",
          }}
        >
          <label
            style={{
              backgroundColor: "var(--light-blue-label-theme-color)",
              borderRadius: "6px",
              padding: "5px 15px",
              textAlign: "left",
              width: "300px",
              height: "36px",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Salary pay run summary
          </label>
        </div>

        <div
          style={{
            marginTop: "10px",
            border: "1px solid #ccc",
            padding: "15px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
          }}
        >
          <div style={{ width: "50%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "stretch",
                padding: "10px 0",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "var(--light-blue-label-theme-color)",
                  borderRadius: "6px",
                  padding: "5px 15px",
                  textAlign: "left",
                  height: "70px",
                  fontWeight: "bold",
                  fontSize: "17px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Net amount to pay</span>
                <span>{formatNumber(summary?.total_net_salary || "0.00")} Baht</span>
              </div>
            </div>
            {[
              { label: "Total Salary", value: summary?.total_salary },
              {
                label: "Add Summary of revenue items",
                value: summary?.non_salary_revenue,
                color: "green",
              },
              {
                label: "Less Summary of deduction items",
                value: summary?.non_social_security_deduct,
                color: "red",
                parentheses: true,
              },
              {
                label: "Less Social Security",
                value: formatNumber(employeeSocialSec.toString()),
                color: "red",
              },
              {
                label: "Less Withholding Tax",
                value: summary?.total_with_holding_tax,
                color: "red",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  width: "100%",
                  fontWeight: "bold",
                  paddingBottom: "10px",
                  margin: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: item.color || "inherit",
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    backgroundColor: "#ececec",
                    borderRadius: "6px",
                    textAlign: "right",
                    padding: "5px",
                    minWidth: "250px",
                    color: "black",
                  }}
                >
                  {(() => {
                    const val = item.value || "0.00";
                    if (item.parentheses) {
                      return val !== "0.00" ? `(${val})` : "(0.00)";
                    }
                    return val !== "0.00" ? val : "0.00";
                  })()}{" "}
                  Baht
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              width: "50%",
              paddingLeft: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ marginTop: "auto", padding: "15px", fontWeight: "bold" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "10px",
                }}
              >
                <span style={{ color: "black" }}>Social Security (Employer)</span>
                <span
                  style={{
                    width: "250px",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f0f0f0",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    display: "inline-block",
                  }}
                >
                  {formatNumber(employerValue)} Baht
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "10px",
                }}
              >
                <span style={{ color: "red" }}>Payable Social Security</span>
                <span
                  style={{
                    backgroundColor: "#ececec",
                    borderRadius: "6px",
                    textAlign: "right",
                    padding: "5px",
                    minWidth: "250px",
                    color: "black",
                  }}
                >
                  {formatNumber(payableSocialSec)} Baht
                </span>
              </div>

              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span style={{ color: "red" }}>Payable Withholding Tax</span>
                <span
                  style={{
                    backgroundColor: "#ececec",
                    borderRadius: "6px",
                    textAlign: "right",
                    padding: "5px",
                    minWidth: "250px",
                    color: "black",
                  }}
                >
                  {formatNumber(
                    parseFloat((summary?.total_with_holding_tax || "0").replace(/,/g, "")) || 0
                  )}{" "}
                  Baht
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}
        >
          <Button key="close" color="grey" onClick={() => setOpenCreateSalaryModal(false)}>
            Close
          </Button>
        </div>
      </div>
    );
  };

  const [bankAccount, setBankAccount] = useState<number | null>(
    props.PayrollSequence?.detailSetting?.[0]?.bank_account_for_pay_run ?? null
  );
  const [paidViaBank, setPaidViaBank] = useState<number | null>(
    props.PayrollSequence?.detailSetting?.[0]?.bank_account_for_pay_run ?? null
  );

  useEffect(() => {
    const defaultBankId = props.PayrollSequence?.detailSetting?.[0]?.bank_account_for_pay_run;
    if (defaultBankId) {
      const selectedAccount = props.PayrollSequence?.bankAccount?.find(
        (acc: any) => acc.id === defaultBankId
      );

      if (selectedAccount?.bank_account_number) {
        const digitsArray = String(selectedAccount.bank_account_number)
          .replace(/\D/g, "")
          .slice(0, 20)
          .split("");
        setBankPaymentDigits(digitsArray);
        setPaidViaDigits(digitsArray);
        setBankAccount(defaultBankId);
        setPaidViaBank(defaultBankId);
        setSelectedBankAccountId(defaultBankId);
      }
    }
  }, [props.PayrollSequence?.detailSetting, props.PayrollSequence?.bankAccount]);

  const [paymentDate, setPaymentDate] = useState<string>("");

  useEffect(() => {
    if (!selectedRun) return;

    const selectedSalaryPayRun = props.PayrollSequence?.salaryPayRunList?.find(
      (item: any) => item.id === selectedRun.id
    );

    if (selectedSalaryPayRun?.due_date) {
      setPaymentDate(selectedSalaryPayRun.due_date);
    } else {
      setPaymentDate("");
    }
  }, [props.PayrollSequence?.salaryPayRunList, selectedRun?.id]);

  const handleFetchPaidSSO = () => {
    props.runSequence({
      sequence: "Payroll",
      action: "FETCH_PAID_SSO",
      pk: selectedRun.id,
    });
  };

  const handleSaveCancelReasonAndDelete = async () => {
    if (!pendingCancelRun) return;

    const updatedPayRun = {
      ...pendingCancelRun,
      cancel_reason: cancelReason,
      issued_date: formatForBackend(pendingCancelRun.issued_date),
      cycle_date_start: formatForBackend(pendingCancelRun.cycle_date_start),
      cycle_date_end: formatForBackend(pendingCancelRun.cycle_date_end),
      due_date: formatForBackend(pendingCancelRun.due_date),
    };

    console.log("Updating salary pay run with cancel_reason before delete:", updatedPayRun);

    const alreadyHasCancelReason =
      Array.isArray(pendingCancelRun.cancel_reason) && pendingCancelRun.cancel_reason.length > 0;

    if (!alreadyHasCancelReason) {
      await handleEditSalaryPayRun(null, { value: null }, updatedPayRun);

      await new Promise<void>((resolve) => {
        const interval = setInterval(() => {
          const editSuccess =
            Array.isArray(updatedPayRun.cancel_reason) && updatedPayRun.cancel_reason.length > 0;
          if (editSuccess) {
            clearInterval(interval);
            resolve();
          }
        }, 200);
      });
    }

    await handleDeleteSalaryPayRun(null, { value: null }, { id: pendingCancelRun.id });

    setIsDeleting(true);

    setTimeout(() => {
      setOpenCancelReasonModal(false);
      setCancelReason([]);
      setPendingCancelRun(null);
      setLastCancelledPayRunCode(pendingCancelRun?.pay_run_code);
      setCancelModalOpen(true);
      setIsDeleting(false);
    }, 1000);
  };

  const handleOpenRevertModal = (payRun: any, actionKey: any) => {
    setPendingRevertRun(payRun);
    setPendingRevertActionKey(actionKey);
    setRevertReason([]);
    setOpenRevertReasonModal(true);
  };

  const handleSaveRevertReasonAndEdit = () => {
    if (!pendingRevertRun || !pendingRevertActionKey) return;

    let revertedStatus = pendingRevertRun.status;
    if (pendingRevertActionKey === "CANCEL_WAIT_APPROVE") {
      revertedStatus = "DRAFT";
    }
    if (pendingRevertActionKey === "CANCEL_WAIT_FINAL_APPROVE") {
      revertedStatus = "DRAFT";
    }
    if (pendingRevertActionKey === "CANCEL_FINAL_APPROVE") {
      revertedStatus = "WAIT_FINAL_APPROVE";
    }

    const updatedPayRun = {
      ...pendingRevertRun,
      status: revertedStatus,
      note: revertReason,
      issued_date: formatForBackend(pendingRevertRun.issued_date),
      cycle_date_start: formatForBackend(pendingRevertRun.cycle_date_start),
      cycle_date_end: formatForBackend(pendingRevertRun.cycle_date_end),
      due_date: formatForBackend(pendingRevertRun.due_date),
    };

    triggerAction(pendingRevertActionKey, () =>
      handleEditSalaryPayRun(null, { value: null }, updatedPayRun)
    );

    setIsReverting(true);

    setTimeout(() => {
      setOpenRevertReasonModal(false);
      setRevertReason([]);
      setPendingRevertRun(null);
      setPendingRevertActionKey(null);
      setIsReverting(false);
    }, 1000);
  };

  const fetchSalaryPayRunUpdateLogList = (
    _e: any,
    { salaryPayRun }: { salaryPayRun: number | string }
  ) => {
    if (!salaryPayRun) return;

    setSubmittedActionKey(BTN_ACTS.FETCH_SALARY_PAYRUN_UPDATE_LOG_LIST);

    props.runSequence({
      sequence: "Payroll",
      action: "FETCH_SALARY_PAYRUN_UPDATE_LOG_LIST",
      salaryPayRun: salaryPayRun,
    });
  };

  useEffect(() => {
    if (!selectedRun) return;

    fetchSalaryPayRunUpdateLogList(null, { salaryPayRun: selectedRun.id });
  }, [selectedRun]);

  const termSet = props.PayrollSequence?.clinicalTermSetList?.salary_payrun_cancel;
  const termSetItems = termSet?.items || [];

  const dropdownOptions = [
    ...termSetItems.map((item: any) => ({
      key: item.id,
      value: item.name,
      text: item.name,
    })),
    ...cancelReason
      .filter((name) => !termSetItems.some((item: any) => item.name === name))
      .map((name) => ({ key: name, value: name, text: name })),
    ...revertReason
      .filter((name) => !termSetItems.some((item: any) => item.name === name))
      .map((name) => ({ key: name, value: name, text: name })),
  ];

  const renderSalaryPayment = (
    payRunCode?: string,
    payRunId?: number,
    employerSocialSec?: string
  ) => {
    if (!payRunCode) {
      return null;
    }

    const selectedSalaryPayRun = props.PayrollSequence?.salaryPayRunList?.find(
      (item: any) => item.id === payRunId
    );

    console.log("selected salary pay run", selectedSalaryPayRun);

    const summary = props.PayrollSequence?.searchResultSummary;
    console.log("data os salary payment", selectedSalaryPayRun, summary);

    const netAmount = parseFloat((summary?.total_net_salary || "0").replace(/,/g, ""));
    const bankAccountNetAmount = parseFloat(
      (summary?.bank_account_total_net_salary || "0").replace(/,/g, "")
    );
    const socialSecEmployer = parseFloat(employerSocialSec || "0");
    const socialSecEmployee = Math.abs(
      parseFloat((summary?.total_social_security_fund || "0").replace(/,/g, ""))
    );
    const withholdingTax = Math.abs(
      parseFloat((summary?.total_with_holding_tax || "0").replace(/,/g, ""))
    );
    const totalSSO = socialSecEmployer + socialSecEmployee;

    const selectedStatus = (selectedSalaryPayRun.status ?? "DRAFT").toUpperCase();

    if (!payRunCode) {
      return null;
    }

    const dueDate = selectedSalaryPayRun?.due_date || "";
    const cycleStart = selectedSalaryPayRun?.cycle_date_start || "";
    const cycleEnd = selectedSalaryPayRun?.cycle_date_end || "";

    console.log("bank account", bankAccount);
    const isPaid = selectedStatus === "PAID";
    const isSSOPaid = selectedRun.sso_paid_status === "PAID";

    const formatBackendDateToDMY = (backendDateStr: string | null | undefined): string => {
      if (!backendDateStr) return "";

      const date = new Date(backendDateStr);
      if (isNaN(date.getTime())) return "";

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    };

    const handleSubmitPaidSalary = () => {
      const data = {
        payment_date: formatForBackend(paymentDate),
        bank_account: bankAccount,
        salary_pay_run: selectedRun.id,
      };

      console.log("Submitting paid salary data:", data);

      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SUBMIT_PAID_SALARY",
        paidSalaryData: data,
      });
    };

    const handleSubmitPaidSSO = () => {
      const data = {
        due_date: formatForBackend(ssoDueDate),
        paid_via: paidViaBank,
        salary_pay_run: selectedRun.id,
      };

      console.log("Submitting paid SSO data:", data);

      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SUBMIT_PAID_SSO",
        paidSSOData: data,
      });
    };

    const handleSubmitRecordAccrued = (salaryPayRunId: number | string) => {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SUBMIT_RECORD_ACCRUED",
        salaryPayRunId,
        accruedData: {},
      });
    };

    const handleSalaryPayRunReport = () => {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SALARY_PAY_RUN_REPORT",
        salaryPayRun: selectedRun.id,
      });
    };

    const handleTaxReport = () => {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_TAX_REPORT",
        salaryPayRun: selectedRun.id,
      });
    };

    const handleBankPaymentFile = () => {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_BANK_PAYMENT_FILE",
        salaryPayRun: selectedRun.id,
        paymentDate: formatToBEDate(paymentDate),
      });
    };

    const HandleSSOReport = () => {
      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SSO_REPORT",
        salaryPayRun: selectedRun.id,
        ssoDueDate: formatToBEDate(ssoDueDate),
      });
    };

    handleFetchPaidSSO();
    console.log("Paid SSO:", props.PayrollSequence?.paidSSOList);
    const filteredPaidSSO = (props.PayrollSequence?.paidSSOList ?? []).filter(
      (item: any) => item.salary_pay_run === selectedRun.id
    );

    console.log("Filtered Paid SSO for selected run:", filteredPaidSSO);
    console.log(
      "Filtered Paid SSO due date for selected run:",
      filteredPaidSSO[filteredPaidSSO.length - 1]?.due_date ?? "",
      formatBackendDateToDMY(filteredPaidSSO[filteredPaidSSO.length - 1]?.due_date ?? "")
    );

    return (
      <>
        <div style={{ padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px" }}>
          <div
            style={{
              marginTop: "10px",
              marginBottom: "20px",
              border: "1px solid #ccc",
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              fontWeight: "bold",
              borderRadius: "6px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}
            >
              <div style={{ minWidth: "220px" }}>Net amount to pay</div>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "200px",
                  textAlign: "right",
                  color: "#333",
                  fontWeight: "normal",
                  marginRight: "50px",
                }}
              >
                {formatNumber(netAmount)} Baht
              </div>
              <ButtonLoadCheck
                color="blue"
                onClick={handleSalaryPayRunReport}
                setProp={props.setProp}
                fluid={false}
                size="medium"
                icon="upload"
                paramKey={BTN_ACTS.HANDLE_SALARY_PAY_RUN_REPORT}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SALARY_PAY_RUN_REPORT]}
                title="Salary Pay Run Report"
              />
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}
            >
              <div style={{ minWidth: "220px" }}>Social Security (Employer)</div>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "200px",
                  textAlign: "right",
                  color: "#333",
                  fontWeight: "normal",
                }}
              >
                {formatNumber(socialSecEmployer)} Baht
              </div>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}
            >
              <div style={{ minWidth: "220px" }}>Social Security (Employee)</div>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "200px",
                  textAlign: "right",
                  color: "#333",
                  fontWeight: "normal",
                }}
              >
                {formatNumber(socialSecEmployee)} Baht
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ minWidth: "220px" }}>Withholding Tax</div>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "200px",
                  textAlign: "right",
                  color: "#333",
                  fontWeight: "normal",
                  marginRight: "50px",
                }}
              >
                {formatNumber(withholdingTax)} Baht
              </div>
              <ButtonLoadCheck
                color="blue"
                onClick={handleTaxReport}
                setProp={props.setProp}
                fluid={false}
                size="medium"
                icon="upload"
                paramKey={BTN_ACTS.HANDLE_TAX_REPORT}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_TAX_REPORT]}
                title="Text file for RD Prep"
              />
            </div>
          </div>

          <div
            style={{
              marginTop: "10px",
              border: "1px solid #ccc",
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              fontWeight: "bold",
              borderRadius: "6px",
            }}
          >
            <label
              style={{
                backgroundColor: "var(--light-blue-label-theme-color)",
                borderRadius: "6px",
                padding: "5px 15px",
                textAlign: "left",
                width: "300px",
              }}
            >
              Create File to Bank
            </label>
            <div style={{ display: "flex", gap: "30px", flexWrap: "wrap", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "80px" }}>Due Date</label>
                <div
                  style={{
                    backgroundColor: "#f0f0f0",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "200px",
                    textAlign: "center",
                    color: "#333",
                    fontWeight: "normal",
                  }}
                >
                  {dueDate}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "80px" }}>Pay Cycle</label>
                <div
                  style={{
                    backgroundColor: "#f0f0f0",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "200px",
                    textAlign: "center",
                    color: "#333",
                    fontWeight: "normal",
                  }}
                >
                  {cycleStart} - {cycleEnd}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "100px" }}>Payment Date</label>
                <DateTextBox
                  placeholder="dd/mm/yyyy"
                  disabled={isPaid}
                  value={isPaid ? selectedRun.payment_date : paymentDate}
                  onChange={(inputDate: string) => {
                    if (isPaid) {
                      return;
                    }

                    const adDate = parseBEDateInput(inputDate);
                    setPaymentDate(adDate);
                  }}
                />
              </div>
            </div>

            <div
              style={{ display: "flex", gap: "5px", alignItems: "center", marginBottom: "20px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "80px" }}>Bank</label>
                <Dropdown
                  selection
                  style={{ width: "300px", marginRight: "50px", fontWeight: "normal" }}
                  value={bankAccount ?? undefined}
                  options={bankAccountOptions}
                  onChange={handleBankPaymentChange}
                  disabled={isPaid}
                />
              </div>
              {bankPaymentDigits.map((digit, index) => {
                const isSpacerIndex =
                  index === 2 || index === 3 || index === 8 || index === 12 || index === 16;
                return (
                  <Input
                    key={`bankPayment-${index}`}
                    id={`BankPayment-Input-${index}`}
                    value={digit}
                    onChange={(e) => onChangeBankPayment(e, index)}
                    onKeyDown={(e: any) => onKeyDownBankPayment(e, index)}
                    disabled={selectedBankAccountId !== null}
                    style={{
                      width: "40px",
                      textAlign: "center",
                      marginRight: isSpacerIndex ? "20px" : "0",
                    }}
                    input={{ type: "text", maxLength: 1 }}
                  />
                );
              })}
            </div>

            <div style={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
              <div style={{ marginRight: "40px" }}>Net amount to pay</div>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  minWidth: "200px",
                  textAlign: "center",
                  color: "#333",
                  fontWeight: "normal",
                  textAlignLast: "right",
                }}
              >
                {formatNumber(bankAccountNetAmount)} Baht
              </div>
            </div>
            <div style={{ display: "flex", marginLeft: "auto", gap: "20px" }}>
              <ButtonLoadCheck
                color="blue"
                onClick={handleBankPaymentFile}
                setProp={props.setProp}
                fluid={false}
                size="medium"
                icon="upload"
                paramKey={BTN_ACTS.HANDLE_BANK_PAYMENT_FILE}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_BANK_PAYMENT_FILE]}
                title="Bank Payment files"
              />
              {selectedStatus === "OUTSTANDING" ? (
                <ButtonLoadCheck
                  color="green"
                  disabled={
                    !selectedRun?.record_accrued_edit_user || !selectedRun?.record_accrued_edited
                  }
                  onClick={() => {
                    if (props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_PAID_SALARY] === "LOADING")
                      return;

                    handleSubmitPaidSalary();
                  }}
                  setProp={props.setProp}
                  fluid={false}
                  size="medium"
                  paramKey={BTN_ACTS.HANDLE_SUBMIT_PAID_SALARY}
                  buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_PAID_SALARY]}
                  title="Confirm Paid Salary"
                />
              ) : null}
            </div>
          </div>

          <div
            style={{
              marginTop: "10px",
              border: "1px solid #ccc",
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              fontWeight: "bold",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                whiteSpace: "nowrap",
                alignItems: "flex-end",
              }}
            >
              <label
                style={{
                  backgroundColor: "var(--light-blue-label-theme-color)",
                  borderRadius: "6px",
                  padding: "5px 15px",
                  textAlign: "left",
                  width: "300px",
                }}
              >
                Paid SSO
              </label>
              {selectedRun?.sso_paid_status == "PAID" ? (
                <label
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    textAlignLast: "center",
                    borderRadius: "6px",
                    padding: "5px 15px",
                    minWidth: "150px",
                    textAlign: "left",
                    fontSize: "15px",
                  }}
                >
                  SSO Paid
                </label>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                gap: "5px",
                flexWrap: "wrap",
                alignItems: "flex-end",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "220px" }}>Due Date</label>
                <DateTextBox
                  placeholder="dd/mm/yyyy"
                  disabled={isSSOPaid}
                  value={
                    isSSOPaid
                      ? formatBackendDateToDMY(
                          filteredPaidSSO[filteredPaidSSO.length - 1]?.due_date ?? ""
                        )
                      : ssoDueDate || formatBackendDateToDMY(selectedRunData?.due_date ?? "")
                  }
                  onChange={(inputDate: string) => {
                    console.log("Input date:", inputDate, ssoDueDate, selectedRun.due_date);
                    if (isSSOPaid) {
                      return;
                    }

                    const adDate = parseBEDateInput(inputDate);
                    console.log("Parsed date:", adDate);
                    setSsoDueDate(adDate);
                  }}
                />
              </div>
            </div>
            <div
              style={{ display: "flex", gap: "5px", alignItems: "center", marginBottom: "20px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "220px" }}>Paid via</label>
                <Dropdown
                  selection
                  style={{ width: "300px", marginRight: "50px", fontWeight: "normal" }}
                  value={paidViaBank ?? undefined}
                  options={bankAccountOptions}
                  onChange={handlePaidViaChange}
                  disabled={isSSOPaid}
                />
              </div>
              {paidViaDigits.map((digit, index) => {
                const isSpacerIndex =
                  index === 2 || index === 3 || index === 8 || index === 12 || index === 16;
                return (
                  <Input
                    key={`paidVia-${index}`}
                    id={`PaidVia-Input-${index}`}
                    value={digit}
                    onChange={(e) => onChangePaidVia(e, index)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      onKeyDownPaidVia(e, index)
                    }
                    disabled={selectedBankAccountId !== null}
                    style={{
                      width: "40px",
                      textAlign: "center",
                      marginRight: isSpacerIndex ? "20px" : "0",
                      backgroundColor: selectedBankAccountId !== null ? "#f0f0f0" : undefined,
                      cursor: selectedBankAccountId !== null ? "not-allowed" : "text",
                    }}
                    input={{ type: "text", maxLength: 1 }}
                  />
                );
              })}
            </div>

            <div
              style={{ fontWeight: "bold", display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}
              >
                <label style={{ minWidth: "220px" }}>SSO Payable</label>
                <div
                  style={{
                    backgroundColor: "#f0f0f0",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "200px",
                    textAlign: "right",
                    color: "#333",
                    fontWeight: "normal",
                  }}
                >
                  {formatNumber(totalSSO)} Baht
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "220px" }}>Total Payment</label>
                <div
                  style={{
                    backgroundColor: "#f0f0f0",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "200px",
                    textAlign: "right",
                    color: "#333",
                    fontWeight: "normal",
                  }}
                >
                  {formatNumber(totalSSO)} Baht
                </div>
              </div>
            </div>
            <div style={{ display: "flex", marginLeft: "auto", gap: "20px" }}>
              <ButtonLoadCheck
                color="blue"
                onClick={HandleSSOReport}
                setProp={props.setProp}
                fluid={false}
                size="medium"
                icon="upload"
                paramKey={BTN_ACTS.HANDLE_SSO_REPORT}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SSO_REPORT]}
                title="Social security files"
              />
              {selectedRun?.sso_paid_status !== "PAID" ? (
                <ButtonLoadCheck
                  color="green"
                  disabled={
                    !selectedRun?.record_accrued_edit_user || !selectedRun?.record_accrued_edited
                  }
                  onClick={() => {
                    if (props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_PAID_SSO] === "LOADING")
                      return;
                    handleSubmitPaidSSO();
                  }}
                  setProp={props.setProp}
                  fluid={false}
                  size="medium"
                  paramKey={BTN_ACTS.HANDLE_SUBMIT_PAID_SSO}
                  buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_PAID_SSO]}
                  title="Confirm Paid SSO"
                />
              ) : null}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              margin: 0,
              padding: 0,
            }}
          >
            {selectedRun?.record_accrued_edit_user && selectedRun?.record_accrued_edited && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <strong>Record by:</strong>
                <span style={{ marginLeft: "0.25rem" }}>
                  {(() => {
                    const user = selectedRun.recorded_by || "Unknown User";
                    const date = new Date(selectedRun.record_accrued_edited);
                    const buddhistYear = date.getFullYear() + 543;
                    const formattedDate = date.toLocaleDateString("th-TH", {
                      day: "2-digit",
                      month: "2-digit",
                    });
                    const formattedTime = date.toLocaleTimeString("th-TH", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    return `${user} ${formattedDate}/${buddhistYear} ${formattedTime}`;
                  })()}
                </span>
              </div>
            )}
            <ButtonLoadCheck
              color="orange"
              disabled={
                !!selectedRun?.record_accrued_edit_user && !!selectedRun?.record_accrued_edited
              }
              onClick={() => {
                if (props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_RECORD_ACCRUED] === "LOADING")
                  return;
                setShowRecordConfirmModal(true);
              }}
              setProp={props.setProp}
              fluid={false}
              size="medium"
              paramKey={BTN_ACTS.HANDLE_SUBMIT_RECORD_ACCRUED}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SUBMIT_RECORD_ACCRUED]}
              title="Record accrued"
            />
            <Button key="close" color="grey" onClick={() => setOpenCreateSalaryModal(false)}>
              Close
            </Button>
          </div>
        </div>
        <ModInfo
          style={{
            width: "37rem",
            margin: "0 auto",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            position: "fixed",
          }}
          backgroundColor="red"
          buttonStyle={{ display: "none" }}
          open={showRecordConfirmModal}
          titleName="Alert message"
          onClose={() => setShowRecordConfirmModal(false)}
        >
          <div style={{ fontWeight: "bold", margin: "2.5rem 0" }}>
            Do you wish to proceed with recording the accrued entries?
          </div>

          <div
            style={{
              display: "flex",
              marginBottom: "0.5rem",
              justifyContent: "center",
              gap: "2rem",
            }}
          >
            <Button
              color="green"
              onClick={async () => {
                await handleSubmitRecordAccrued(selectedRun.id);
                setShowRecordConfirmModal(false);
                handleSalaryPayRunReport();
                handleTaxReport();
                handleBankPaymentFile();
                HandleSSOReport();
              }}
            >
              Confirm
            </Button>
            <Button
              style={{ marginRight: "1rem" }}
              color="red"
              onClick={() => setShowRecordConfirmModal(false)}
            >
              Cancel
            </Button>
          </div>
        </ModInfo>
      </>
    );
  };

  useEffect(() => {
    if (selectedRun && selectedRun.sso_paid_status === "PAID") {
      handleFetchPaidSSO();
    }
  }, [selectedRun?.id, selectedRun?.sso_paid_status]);

  type SearchFilter = {
    payRunCode?: string;
    issuedDateStart?: string;
    issuedDateEnd?: string;
    status?: string;
  };

  const [filteredPayRunCode, setFilteredPayRunCode] = useState<string | null>(null);

  type SalaryPayRunTableProps = {
    data?: any[];
    onEdit?: (payRunCode: string, e: any) => void;
    columns?: string[];
    onRowSelect?: (code: string) => void;
    selectedRowCode?: string;
    PayrollSequence: any;
    plainStatus?: boolean;
    cellAlign?: "left" | "center" | "right";
    buttonLoadCheck: any;
  };

  const columnLabels: Record<string, string> = {
    payRunCode: "Pay Run Code",
    issuedDate: "Issued Date",
    status: "Status",
    cycleDate: "Cycle Date",
    paymentDate: "Payment Date",
    employees: "Number of Employees",
    amount: "Payment Amount",
    actions: "",
    ssoPayment: "SSO Payment",
  };

  const [activePage, setActivePage] = useState(1);
  const rowsPerPage = 15;
  const totalPages = Math.max(1, Math.ceil(salaryPayRunList.length / rowsPerPage));

  const handleSalaryPayRunReport = (id: number) => {
    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_SALARY_PAY_RUN_REPORT",
      salaryPayRun: id,
    });
  };

  const SalaryPayRunTable: React.FC<SalaryPayRunTableProps> = (props) => {
    const { onEdit, columns, onRowSelect, selectedRowCode } = props;

    const activeColumns = columns ?? [
      "payRunCode",
      "issuedDate",
      "cycleDate",
      "paymentDate",
      "employees",
      "amount",
      "status",
      "ssoPayment",
      "actions",
    ];

    const headerStyle = {
      backgroundColor: "#add8d6",
      border: "1px solid #82c8e5",
      whiteSpace: "nowrap" as const,
      textAlign: "center",
    };
    const alignment = props.cellAlign ?? "left";
    const cellStyle = { whiteSpace: "nowrap", padding: "4px 8px", textAlign: alignment };
    const amountStyle = { ...cellStyle, textAlign: "right" };

    const startIdx = (activePage - 1) * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    const pageData = salaryPayRunList.slice(startIdx, endIdx);

    const paddedData = [...pageData];
    while (paddedData.length < rowsPerPage) paddedData.push({ isEmpty: true });

    return (
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table celled compact style={{ width: "100%" }}>
          <Table.Header>
            <Table.Row>
              {activeColumns.map((col) => (
                <Table.HeaderCell key={col} style={headerStyle}>
                  {columnLabels[col]}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paddedData.map((item, index) =>
              "isEmpty" in item ? (
                <Table.Row key={`empty-${index}`}>
                  {activeColumns.map((_, colIdx) => (
                    <Table.Cell key={colIdx}>&nbsp;</Table.Cell>
                  ))}
                </Table.Row>
              ) : (
                <Table.Row
                  key={index}
                  onClick={() => onRowSelect?.(item.pay_run_code)}
                  style={{
                    cursor: onRowSelect ? "pointer" : undefined,
                    backgroundColor:
                      item.pay_run_code === selectedRowCode ? "#e6f7ff" : "transparent",
                    transition: "background-color 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e: any) => {
                    if (item.pay_run_code !== selectedRowCode) {
                      e.currentTarget.style.backgroundColor = "#eaf6ff";
                    }
                  }}
                  onMouseLeave={(e: any) => {
                    if (item.pay_run_code !== selectedRowCode) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {activeColumns.map((col) => {
                    const status = item.status?.toUpperCase();
                    const isPrintable = status === "PAID" || status === "OUTSTANDING";

                    if (col === "actions") {
                      return (
                        <Table.Cell key="actions" style={{ ...cellStyle, textAlign: "center" }}>
                          <Icon
                            name="edit"
                            color="yellow"
                            size="large"
                            style={{ cursor: "pointer" }}
                            onClick={(e: any) => {
                              e.stopPropagation();
                              onEdit?.(item.pay_run_code, e);
                            }}
                          />

                          <span
                            style={{
                              display: "inline-block",
                              ...(props.buttonLoadCheck?.[item.id] === "LOADING" && {
                                transform: "translateY(50%)",
                              }),
                            }}
                          >
                            <Icon
                              name={
                                props.buttonLoadCheck?.[item.id] === "LOADING"
                                  ? "circle notch"
                                  : "print"
                              }
                              loading={props.buttonLoadCheck?.[item.id] === "LOADING"}
                              color={isPrintable ? "blue" : "grey"}
                              size="large"
                              style={{ cursor: isPrintable ? "pointer" : "default" }}
                              onClick={(e: { stopPropagation: () => void }) => {
                                if (isPrintable) {
                                  handleSalaryPayRunReport(item.id);
                                  e.stopPropagation();
                                }
                              }}
                            />
                          </span>
                        </Table.Cell>
                      );
                    }
                    if (col === "amount") {
                      return (
                        <Table.Cell key={col} style={amountStyle}>
                          {item.payment_amount}
                        </Table.Cell>
                      );
                    }
                    if (col === "cycleDate") {
                      return (
                        <Table.Cell key={col} style={cellStyle}>
                          {item.cycle_date_start} - {item.cycle_date_end}
                        </Table.Cell>
                      );
                    }
                    if (col === "payRunCode") {
                      return (
                        <Table.Cell key={col} style={cellStyle}>
                          {item.pay_run_code}
                        </Table.Cell>
                      );
                    }
                    if (col === "issuedDate") {
                      return (
                        <Table.Cell key={col} style={cellStyle}>
                          {item.issued_date}
                        </Table.Cell>
                      );
                    }
                    if (col === "paymentDate") {
                      return (
                        <Table.Cell key={col} style={cellStyle}>
                          {item.payment_date}
                        </Table.Cell>
                      );
                    }
                    if (col === "employees") {
                      return (
                        <Table.Cell key={col} style={{ ...cellStyle, textAlign: "center" }}>
                          {item.employee_amount ?? 0}
                        </Table.Cell>
                      );
                    }

                    if (col === "status") {
                      const label = statusTextMap[item.status?.toUpperCase()] ?? item.status;
                      return (
                        <Table.Cell key={col} style={{ ...cellStyle, textAlign: "center" }}>
                          {props.plainStatus ? (
                            label
                          ) : (
                            <div style={getStatusStyle(item.status)}>{label}</div>
                          )}
                        </Table.Cell>
                      );
                    }
                    if (col === "ssoPayment") {
                      let content = null;

                      if (!item.record_accrued_edit_user || !item.record_accrued_edited) {
                        content = null;
                      } else if (item.sso_paid_status === "NOT_PAID") {
                        content = (
                          <Icon
                            name="close"
                            circular
                            inverted
                            color="red"
                            style={{ fontSize: "1rem" }}
                          />
                        );
                      } else if (item.sso_paid_status === "PAID") {
                        content = (
                          <Icon
                            name="check"
                            circular
                            inverted
                            color="green"
                            style={{ fontSize: "1rem" }}
                          />
                        );
                      }

                      return (
                        <Table.Cell key={col} style={{ ...cellStyle, textAlign: "center" }}>
                          {content}
                        </Table.Cell>
                      );
                    }
                    return (
                      <Table.Cell key={col} style={cellStyle}>
                        {item[col] ?? ""}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              )
            )}
          </Table.Body>
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell
                colSpan={activeColumns.length}
                style={{ backgroundColor: "#f2f2f2", textAlign: "center" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Button
                    icon="chevron left"
                    size="mini"
                    disabled={activePage === 1}
                    onClick={() => setActivePage((prev) => Math.max(1, prev - 1))}
                  />
                  <span style={{ fontWeight: "bold" }}>
                    {activePage} / {totalPages}
                  </span>
                  <Button
                    icon="chevron right"
                    size="mini"
                    disabled={activePage === totalPages}
                    onClick={() => setActivePage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    );
  };

  const [selectedRowCode, setSelectedRowCode] = useState<string | null>(null);

  const [payRunCodeSearch, setPayRunCodeSearch] = useState("");
  const [issuedDateSearch, setIssuedDateSearch] = useState("");
  const [statusSearch, setStatusSearch] = useState("");

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const [searchFilters, setSearchFilters] = useState<SearchFilter | null>(null);

  const [includeIssuedDate, setIncludeIssuedDate] = useState(false);
  const [includeStatus, setIncludeStatus] = useState(false);

  const [issuedDateStart, setIssuedDateStart] = useState("");
  const [issuedDateEnd, setIssuedDateEnd] = useState("");

  const [onlyAccruedRecording, setOnlyAccruedRecording] = useState(false);

  const renderSalaryPayRun = () => {
    if (activeSalaryTab !== "SALARYPAYRUN") {
      return null;
    }

    return (
      <div style={{ padding: "20px 0" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: "20px",
            width: "100%",
            paddingBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Pay Run Code</label>
            <Input
              placeholder="Search by Pay Run Code"
              style={{ width: "300px" }}
              value={payRunCodeSearch}
              onChange={(e, data) => setPayRunCodeSearch(data.value)}
              action={{
                icon: "search",
                onClick: () => {
                  setSearchFilters({
                    payRunCode: payRunCodeSearch.trim(),
                    issuedDateStart,
                    issuedDateEnd,
                    status: statusSearch,
                  });
                  setIsSearchModalOpen(true);
                },
                style: {
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                },
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox
              checked={includeIssuedDate}
              onChange={() => {
                const newValue = !includeIssuedDate;
                setIncludeIssuedDate(newValue);
                if (!newValue) {
                  setIssuedDateStart("");
                  setIssuedDateEnd("");
                }
              }}
            />
            <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Issued Date</label>
            <DateTextBox
              placeholder="Start (dd/mm/yyyy)"
              value={issuedDateStart}
              onChange={(dateStr: string) =>
                setIssuedDateStart(
                  dateStr && dateStr.includes("/") ? parseBEDateInput(dateStr) : ""
                )
              }
              inputStyle={{ width: "130px" }}
              disabled={!includeIssuedDate}
            />
            <DateTextBox
              placeholder="End (dd/mm/yyyy)"
              value={issuedDateEnd}
              onChange={(dateStr: string) =>
                setIssuedDateEnd(dateStr && dateStr.includes("/") ? parseBEDateInput(dateStr) : "")
              }
              inputStyle={{ width: "130px" }}
              disabled={!includeIssuedDate}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox
              checked={includeStatus}
              onChange={() => {
                const newValue = !includeStatus;
                setIncludeStatus(newValue);
                if (!newValue) {
                  setStatusSearch("");
                }
              }}
            />
            <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Status</label>
            <Dropdown
              selection
              options={[
                { key: "draft", text: "Draft", value: "DRAFT" },
                { key: "wait_approve", text: "Wait Approve", value: "WAIT_APPROVE" },
                { key: "wait_final", text: "Wait Final Approve", value: "WAIT_FINAL_APPROVE" },
                { key: "outstanding", text: "Outstanding", value: "OUTSTANDING" },
                { key: "paid", text: "Paid", value: "PAID" },
              ]}
              value={statusSearch}
              onChange={(e, { value }) => setStatusSearch(value as string)}
              style={{ width: "250px" }}
              disabled={!includeStatus}
            />
          </div>
          <ButtonLoadCheck
            color="blue"
            onClick={(e) =>
              handleSearchSalaryPayRun(e, {
                value: {
                  payRunCode: payRunCodeSearch.trim(),
                  issuedDateStart: includeIssuedDate ? formatToBEDate(issuedDateStart) : "",
                  issuedDateEnd: includeIssuedDate ? formatToBEDate(issuedDateEnd) : "",
                  status: includeStatus ? statusSearch : "",
                },
              })
            }
            setProp={props.setProp}
            size="medium"
            fluid={false}
            paramKey={BTN_ACTS.HANDLE_SEARCH_SALARY_PAY_RUN}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SEARCH_SALARY_PAY_RUN]}
            title="Search"
          />
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox
              checked={onlyAccruedRecording}
              onChange={(_, { checked }) => setOnlyAccruedRecording(!!checked)}
            />
            <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
              Only Accrued Recording
            </label>
          </div>
        </div>

        <SalaryPayRunTable
          data={
            filteredPayRunCode
              ? salaryPayRunList.filter((run: any) => run.pay_run_code === filteredPayRunCode)
              : salaryPayRunList
          }
          onEdit={(code, e) => {
            const selected = salaryPayRunList.find((item: any) => item.pay_run_code === code);
            setSelectedPayRun(code);
            setSelectedStatus(selected?.status);
            setOpenCreateSalaryModal(true);
            handleSearchEmployeePayroll(e, { salaryPayRun: selected?.id });
          }}
          PayrollSequence={props.PayrollSequence}
          buttonLoadCheck={props.buttonLoadCheck}
        />
      </div>
    );
  };

  useEffect(() => {
    handleSearchSalaryPayRun(null, {
      value: {
        payRunCode: payRunCodeSearch.trim(),
        issuedDateStart: includeIssuedDate ? formatToBEDate(issuedDateStart) : "",
        issuedDateEnd: includeIssuedDate ? formatToBEDate(issuedDateEnd) : "",
        status: includeStatus ? statusSearch : "",
        onlyAccrued: onlyAccruedRecording ? "true" : "false",
      },
    });
  }, [onlyAccruedRecording]);

  type SalaryStatus = "DRAFT" | "WAIT_APPROVE" | "WAIT_FINAL_APPROVE" | "OUTSTANDING" | "PAID";
  type EditSalaryTab = "SALARY_DETAIL" | "EDIT_SALARY" | "PAYMENT";

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  useEffect(() => {
    const selected = salaryPayRunList.find((item: any) => item.pay_run_code === selectedPayRun);
    setSelectedStatus(selected?.status?.toUpperCase());
  }, [selectedPayRun, salaryPayRunList]);
  useEffect(() => {
    if (openCreateSalaryModal) {
      setActiveEditSalaryTab(
        selectedStatus === "OUTSTANDING" || selectedStatus === "PAID"
          ? "SALARY_DETAIL"
          : "EDIT_SALARY"
      );
    }
  }, [openCreateSalaryModal, selectedStatus]);

  const salaryEditTabButtonStyle = (targetType: EditSalaryTab) => ({
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
    padding: "10px 16px",
    fontWeight: "bold",
    backgroundColor: activeEditSalaryTab === targetType ? "#2185d0" : undefined,
    color: activeEditSalaryTab === targetType ? "white" : undefined,
    borderBottom: "none",
  });

  console.log("Search Criteria: ", searchCriteria);

  const formatToThaiDate = (gregDate: string): string => {
    const parts = gregDate.split("/");
    if (parts.length !== 3) {
      return "";
    }

    const [day, month, year] = parts;
    const thaiYear = parseInt(year) + 543;

    return [day, month, thaiYear].join("/");
  };

  const fetchEmployeePayrollList = (_e: any, { value, salaryPayRun }: any) => {
    const { startDate, endDate, department, position } = value;
    const startDateThai = formatToThaiDate(startDate);
    const endDateThai = formatToThaiDate(endDate);

    if (activeSalaryTab === "CREATESALARY") setHasSearchedCreate(true);
    if (activeSalaryTab === "SALARYPAYRUN") setHasSearchedEdit(true);
    setSubmittedActionKey(BTN_ACTS.FETCH_EMPLOYEE_PAYROLL_LIST);

    props.runSequence({
      sequence: "Payroll",
      action: "FETCH_EMPLOYEE_PAYROLL_LIST",
      startDate: startDateThai,
      endDate: endDateThai,
      department,
      position,
      salaryPayRun: salaryPayRun ?? null,
    });
  };

  const handleSearchEmployeePayroll = (_e: any, { salaryPayRun }: any) => {
    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_SEARCH_EMPLOYEE_PAYROLL",
      salaryPayRun,
    });
  };

  const handleSearchSalaryPayRun = (_e: any, { value }: any) => {
    const { payRunCode, issuedDateStart, issuedDateEnd, status } = value;
    const onlyAccrued = onlyAccruedRecording ? "true" : "false";
    console.log(
      "salary pay run searching data",
      payRunCode,
      issuedDateStart,
      issuedDateEnd,
      status,
      onlyAccrued
    );

    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_SEARCH_SALARY_PAY_RUN",
      payRunCode,
      issuedDateStart,
      issuedDateEnd,
      status,
      onlyAccrued,
    });
  };

  const formatToBEDate = (adDate: string): string => {
    const [dd, mm, yyyy] = adDate.split("/");
    const beYear = String(parseInt(yyyy) + 543);
    return `${dd}/${mm}/${beYear}`;
  };

  const parseBEDateInput = (beDate: string): string => {
    if (!beDate || beDate.trim() === "") {
      return "";
    }
    const [dd, mm, yyyyBE] = beDate.split("/");
    if (!dd || !mm || !yyyyBE) {
      return "";
    }
    const adYear = String(parseInt(yyyyBE) - 543);
    return `${dd}/${mm}/${adYear}`;
  };

  const formatForBackend = (adDate: string): string => {
    const [dd, mm, yyyy] = adDate.split("/");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [submittedActionKey, setSubmittedActionKey] = useState<string | null>(null);
  const [showSnack, setShowSnack] = useState(true);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setShowSnack(true);
    }
  }, [props.errorMessage?.[CARD_NAME], props.successMessage?.[CARD_NAME]]);

  const handleSnackClose = (_event?: any, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setShowSnack(false);
    props.setProp(`errorMessage.${CARD_NAME}`, null);
    props.setProp(`successMessage.${CARD_NAME}`, null);
  };

  console.log("SUCCESS", props.successMessage?.[CARD_NAME]);

  const handleEditEmployeePayroll = (
    _e: any,
    { value }: any,
    item: { id: number; [key: string]: any }
  ) => {
    console.log("Edit Employee Data:", item);
    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_EDIT_EMPLOYEE_PAYROLL",
      item,
    });
  };

  const handleEditSalaryPayRun = (
    _e: any,
    { value }: any,
    salaryPayRun: { id: number; [key: string]: any }
  ) => {
    console.log("Editing salary pay run data:", salaryPayRun, "with value:", value);
    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_EDIT_SALARY_PAY_RUN",
      salaryPayRun,
    });
    resetEditSalaryState();
  };

  const [deleteRequestId, setDeleteRequestId] = useState<number | null>(null);

  const handleDeleteSalaryPayRun = (
    _e: any,
    _data: any,
    salaryPayRun: { id: number; [key: string]: any }
  ) => {
    console.log("Deleting salary pay run:", salaryPayRun);

    setDeleteRequestId(salaryPayRun.id);
    setLastCancelledPayRunCode(selectedRun?.pay_run_code || "");

    props.runSequence({
      sequence: "Payroll",
      action: "HANDLE_DELETE_SALARY_PAY_RUN",
      salaryPayRun,
    });
  };

  useEffect(() => {
    const isDeleting = deleteRequestId !== null;
    const isSuccess = props.successMessage?.Payroll === "Salary pay run deleted successfully";
    const isNotLoading = !props.buttonLoadCheck?.[BTN_ACTS.HANDLE_DELETE_SALARY_PAY_RUN];

    if (isDeleting && isSuccess && isNotLoading) {
      setOpenCreateSalaryModal(false);
      setDeleteRequestId(null);
    }
  }, [
    deleteRequestId,
    props.successMessage?.Payroll,
    props.buttonLoadCheck?.[BTN_ACTS.HANDLE_DELETE_SALARY_PAY_RUN],
  ]);

  console.log(employeeList, summary);
  console.log("salary pay run list:", props.PayrollSequence?.salaryPayRunList);

  const bankAccountOptions = (props.PayrollSequence?.bankAccount ?? []).map((account: any) => {
    const bank = props.PayrollSequence?.bankList?.find((b: any) => b.id === account.bank);
    const accountTypeMap: Record<string, string> = {
      SAVINGS_ACCOUNT: "Savings Account",
      FIXED_DEPOSIT_ACCOUNT: "Fixed Deposit Account",
      MONTHLY_FIXED_DEPOSIT_ACCOUNT: "Monthly Fixed Deposit Account",
      CURRENT_ACCOUNT: "Current Account",
      INTERNATIONAL_CURRENCY_ACCOUNT: "International Currency Account",
    };

    return {
      key: account.id,
      value: account.id,
      text: `${bank?.name} ${accountTypeMap[account.bank_account_type]} ${
        account.bank_account_number
      }`,
    };
  });

  const [selectedEmployee, setSelectedEmployee] = React.useState<any>(null);

  const [ot1, setOt1] = React.useState("");
  const [ot15, setOt15] = React.useState("");
  const [ot3, setOt3] = React.useState("");
  const [base1, setBase1] = React.useState("");
  const [base15, setBase15] = React.useState("");
  const [base3, setBase3] = React.useState("");

  const [openOtReasonModal, setOpenOtReasonModal] = useState(false);
  const [otReason, setOtReason] = useState("");
  const [pendingOtEdit, setPendingOtEdit] = useState<any>(null);

  const openModalForEmployee = (employee: any) => {
    setSelectedEmployee(employee);
    setOt1(employee?.ot_1hr || "");
    setOt15(employee?.ot_1_5hr || "");
    setOt3(employee?.ot_3hr || "");
    setBase1(employee?.base_ot_1hr || "");
    setBase15(employee?.base_ot_1_5hr || "");
    setBase3(employee?.base_ot_3hr || "");
    setOpenOvertimeModal(true);
  };

  useEffect(() => {
    const firstSetting = props.PayrollSequence?.detailSetting?.[0];

    if (firstSetting?.pay_run_date !== null && firstSetting?.pay_run_date !== undefined) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();

      let dueDate;

      if (firstSetting.pay_run_date === 99) {
        dueDate = new Date(currentYear, currentMonth + 1, 0);
      } else {
        const runDay = Number(firstSetting.pay_run_date);
        dueDate = new Date(currentYear, currentMonth, runDay);

        if (today > dueDate) {
          dueDate = new Date(currentYear, currentMonth + 1, runDay);
        }
      }

      const formattedDueDate = dueDate
        .toLocaleDateString("en-GB")
        .split("/")
        .map((part) => part.padStart(2, "0"))
        .join("/");

      setSalaryDueDate(formattedDueDate);
      setIsDueDateComputed(true);
      console.log("Computed salary due date:", formattedDueDate);
    }
  }, [props.PayrollSequence?.detailSetting?.[0]?.pay_run_date]);

  useEffect(() => {
    if (!isSearchModalOpen) {
      setIssuedDateSearch("");
      setSelectedRowCode(null);
      setSearchFilters({
        payRunCode: payRunCodeSearch,
        issuedDateStart: "",
        issuedDateEnd: "",
        status: "",
      });

      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SEARCH_SALARY_PAY_RUN",
        payRunCode: "",
        issuedDateStart: "",
        issuedDateEnd: "",
        status: "",
        onlyAccrued: "false",
      });
    }
  }, [isSearchModalOpen]);

  useEffect(() => {
    if (isSearchModalOpen) {
      handleSearchSalaryPayRun(null, {
        value: {
          payRunCode: "",
          issuedDateStart: "",
          issuedDateEnd: "",
          status: "",
        },
      });
    }
  }, [isSearchModalOpen]);

  useEffect(() => {
    if (activeSalaryTab === "SALARYPAYRUN") {
      setPayRunCodeSearch("");
      setIssuedDateSearch("");
      setIssuedDateStart("");
      setIssuedDateEnd("");
      setIncludeIssuedDate(false);
      setStatusSearch("");
      setIncludeStatus(false);

      props.runSequence({
        sequence: "Payroll",
        action: "HANDLE_SEARCH_SALARY_PAY_RUN",
        payRunCode: "",
        issuedDateStart: "",
        issuedDateEnd: "",
        status: "",
        onlyAccrued: "false",
      });
    }
  }, [activeSalaryTab]);

  return (
    <>
      <CardPayrollSalaryTabUX
        languageUX={props.languageUX}
        renderSalaryTab={renderSalaryTab}
        onClickCreateSalary={() => setActiveSalaryTab("CREATESALARY")}
        onClickSalaryPayRun={() => setActiveSalaryTab("SALARYPAYRUN")}
        getCreateSalaryTabStyle={getSalaryTabStyle("CREATESALARY")}
        getSalaryPayRunTabStyle={getSalaryTabStyle("SALARYPAYRUN")}
        activeSalaryTab={activeSalaryTab}
      />
      <Modal
        open={openOvertimeModal}
        onClose={() => {
          setOpenOvertimeModal(false);
          setSelectedEmployee(null);
        }}
        size="large"
      >
        <Modal.Header style={{ backgroundColor: "transparent", padding: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                backgroundColor: "#f2711c",
                color: "white",
                display: "inline-block",
                padding: "6px 12px",
                borderRadius: "4px",
                fontWeight: "bold",
              }}
            >
              Edit Over Time Details
            </div>

            {selectedEmployee && (
              <div
                style={{
                  backgroundColor: "#ffdbbb",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#555",
                  display: "inline-flex",
                  gap: "16px",
                }}
              >
                <div>[{selectedEmployee.employee_code || "-"}]</div>
                <div>{selectedEmployee.employee_full_name || "-"}</div>
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Content>
          {(() => {
            if (!selectedEmployee) {
              return null;
            }

            const canEdit =
              activeSalaryTab === "CREATESALARY" ||
              selectedStatus === "DRAFT" ||
              selectedStatus === "WAIT_APPROVE";

            const otDetails = [
              { label: "OT 1.0", hour: ot1, base: base1, factor: 1.0 },
              { label: "OT 1.5", hour: ot15, base: base15, factor: 1.5 },
              { label: "OT 3.0", hour: ot3, base: base3, factor: 3.0 },
            ];

            const parsed = otDetails.map((ot) => {
              const hour = parseFloat(ot.hour || "0");
              const base = parseFloat(ot.base || "0");
              return {
                ...ot,
                hour,
                base,
                result: hour * ot.factor * base,
              };
            });

            const total = parsed.reduce((sum, row) => sum + row.result, 0);

            return (
              <div style={{ width: "1000px", padding: "20px 0" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  {parsed.map((row, idx) => {
                    const hourStr = idx === 0 ? ot1 : idx === 1 ? ot15 : ot3;
                    const baseStr = idx === 0 ? base1 : idx === 1 ? base15 : base3;
                    const handleDecimalInput =
                      (setter: (val: string) => void) =>
                      (e: React.ChangeEvent<HTMLInputElement>) => {
                        let value = e.target.value;
                        value = value.replace(/[^0-9.]/g, "");
                        const parts = value.split(".");
                        if (parts.length > 2) {
                          return;
                        }
                        if (parts[1]?.length > 2) {
                          parts[1] = parts[1].slice(0, 2);
                        }
                        setter(parts.join("."));
                      };

                    const handleOtChange = (idx: number) =>
                      handleDecimalInput((val) => {
                        if (idx === 0) setOt1(val);
                        else if (idx === 1) setOt15(val);
                        else if (idx === 2) setOt3(val);
                      });

                    const handleBaseChange = (idx: number) =>
                      handleDecimalInput((val) => {
                        if (idx === 0) {
                          setBase1(val);
                        } else if (idx === 1) {
                          setBase15(val);
                        } else if (idx === 2) {
                          setBase3(val);
                        }
                      });

                    return (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "10px",
                          marginBottom: "20px",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Hour</label>
                          <Input
                            type="number"
                            value={hourStr}
                            readOnly={!canEdit}
                            onChange={handleOtChange(idx)}
                            style={{ width: "200px" }}
                            input={{
                              style: {
                                backgroundColor: canEdit ? "#fffcd3" : "#d3d3d3",
                                textAlign: "center",
                              },
                            }}
                          />
                        </div>

                        <div style={{ fontSize: "18px", paddingBottom: "6px" }}>×</div>

                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Factor</label>
                          <Input
                            value={row.factor.toFixed(2)}
                            readOnly
                            style={{ width: "200px" }}
                            input={{ style: { backgroundColor: "#d3d3d3", textAlign: "center" } }}
                          />
                        </div>

                        <div style={{ fontSize: "18px", paddingBottom: "6px" }}>×</div>

                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Base OT</label>
                          <Input
                            type="number"
                            value={baseStr}
                            readOnly={!canEdit}
                            onChange={handleBaseChange(idx)}
                            style={{ width: "200px" }}
                            input={{
                              style: {
                                backgroundColor: canEdit ? "#fffcd3" : "#d3d3d3",
                                textAlign: "center",
                              },
                            }}
                          />
                        </div>

                        <div style={{ fontSize: "18px", paddingBottom: "6px" }}>=</div>

                        <div
                          style={{ display: "flex", flexDirection: "column", textAlign: "center" }}
                        >
                          <label
                            style={{
                              fontWeight: "bold",
                              marginBottom: "4px",
                              visibility: "hidden",
                            }}
                          >
                            Result
                          </label>
                          <Input
                            value={row.result.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            readOnly
                            style={{ width: "200px" }}
                            input={{ style: { backgroundColor: "#d3d3d3", textAlign: "center" } }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      paddingRight: "56px",
                      marginTop: "-10px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <label
                        style={{ fontWeight: "bold", marginBottom: "4px", paddingRight: "80px" }}
                      >
                        Total Overtime
                      </label>
                      <Input
                        value={total.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        readOnly
                        style={{ width: "200px" }}
                        input={{
                          style: {
                            backgroundColor: "#d3d3d3",
                            textAlign: "center",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </Modal.Content>

        <Modal.Actions>
          <Button
            color="green"
            onClick={() => {
              if (!selectedEmployee) {
                return;
              }
              const sanitizeNumber = (value: string | number) => {
                if (typeof value === "number") {
                  return value;
                }
                if (!value) {
                  return 0;
                }
                return parseFloat(value.replace(/,/g, ""));
              };

              const overtime =
                sanitizeNumber(ot1) * 1.0 * sanitizeNumber(base1) +
                sanitizeNumber(ot15) * 1.5 * sanitizeNumber(base15) +
                sanitizeNumber(ot3) * 3.0 * sanitizeNumber(base3);

              const numericFields = [
                "salary",
                "overtime",
                "bonus",
                "position_pay",
                "license_allowance",
                "language_pay",
                "skill_pay",
                "doctor_fee",
                "providence_fund",
                "with_holding_tax",
                "social_security_fund",
                "absence",
                "total_revenue",
                "total_deduct",
              ];

              const sanitizeAllNumbers = (data: any) => {
                const sanitized: any = { ...data };
                numericFields.forEach((field) => {
                  if (sanitized.hasOwnProperty(field)) {
                    const val = sanitized[field];
                    if (typeof val === "string") {
                      sanitized[field] = parseFloat(val.replace(/,/g, "")) || 0;
                    }
                  }
                });
                return sanitized;
              };

              const updatedData = {
                ...selectedEmployee,
                ot_1hr: sanitizeNumber(ot1),
                ot_1_5hr: sanitizeNumber(ot15),
                ot_3hr: sanitizeNumber(ot3),
                base_ot_1hr: sanitizeNumber(base1),
                base_ot_1_5hr: sanitizeNumber(base15),
                base_ot_3hr: sanitizeNumber(base3),
                overtime: parseFloat(overtime.toFixed(2)),
              };

              const sanitizedData = sanitizeAllNumbers(updatedData);

              console.log("Sanitized Data:", sanitizedData);

              const employeeWithCutoff = employeesWithOtCutoff.find(
                (emp: any) => emp.id === selectedEmployee.id
              );

              const otCutOff = employeeWithCutoff?.ot_cut_off ?? Infinity;
              const totalOt = sanitizedData.ot_1hr + sanitizedData.ot_1_5hr + sanitizedData.ot_3hr;

              console.log("totalOT:", totalOt, otCutOff);

              if (totalOt > otCutOff) {
                setPendingOtEdit(sanitizedData);
                setOtReason("");
                setOpenOtReasonModal(true);
                return;
              }

              handleEditEmployeePayroll(null, {}, sanitizedData);
              setOpenOvertimeModal(false);
              setSelectedEmployee(null);
            }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setOpenOvertimeModal(false);
              setSelectedEmployee(null);
            }}
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        open={openCreateSalaryModal}
        onClose={() => {
          resetEditSalaryState();
          setOpenCreateSalaryModal(false);
        }}
        size="fullscreen"
      >
        <Modal.Content>
          {selectedStatus === "OUTSTANDING" || selectedStatus === "PAID" ? (
            <>
              <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
                <Button
                  style={salaryEditTabButtonStyle("SALARY_DETAIL")}
                  onClick={() => setActiveEditSalaryTab("SALARY_DETAIL")}
                >
                  Salary Detail
                </Button>
                <Button
                  style={salaryEditTabButtonStyle("EDIT_SALARY")}
                  onClick={() => setActiveEditSalaryTab("EDIT_SALARY")}
                >
                  Edit Salary Pay Run
                </Button>
                <Button
                  style={salaryEditTabButtonStyle("PAYMENT")}
                  onClick={() => setActiveEditSalaryTab("PAYMENT")}
                >
                  Payment
                </Button>
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #ccc",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  style={salaryEditTabButtonStyle("EDIT_SALARY")}
                  onClick={() => setActiveEditSalaryTab("EDIT_SALARY")}
                >
                  Edit Salary Pay Run
                </Button>
                {selectedStatus === "DRAFT" && (
                  <div style={{ ...getStatusStyle(selectedStatus), marginBottom: "5px" }}>
                    {statusTextMap[selectedStatus] ?? selectedStatus}
                  </div>
                )}
              </div>
            </>
          )}
          {selectedStatus !== "DRAFT" && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "20px",
                  marginBottom: "5px",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {activeEditSalaryTab === "EDIT_SALARY" && (
                    <span style={{ fontWeight: "bold", fontSize: "16px", paddingRight: "10px" }}>
                      Edit Salary Pay Run
                    </span>
                  )}
                  <div
                    style={{
                      backgroundColor: "#ffd580",
                      borderRadius: "6px",
                      padding: "5px 15px",
                      fontWeight: "bold",
                      textAlign: "left",
                      minWidth: "250px",
                    }}
                  >
                    {selectedPayRun}
                  </div>
                </div>
                <div style={getStatusStyle(selectedStatus)}>
                  {statusTextMap[selectedStatus ?? ""] ?? selectedStatus}
                </div>
              </div>
              <hr />
            </>
          )}

          {activeEditSalaryTab === "SALARY_DETAIL" &&
            (selectedStatus === "OUTSTANDING" || selectedStatus === "PAID") && (
              <div>{renderSalaryDetail(selectedPayRun, selectedRun?.id, employerSocialSec)}</div>
            )}
          {activeEditSalaryTab === "EDIT_SALARY" && (
            <div>
              {renderCreateSalary(selectedPayRun, employerSocialSec, setEmployerSocialSec, version)}
            </div>
          )}
          {activeEditSalaryTab === "PAYMENT" &&
            (selectedStatus === "OUTSTANDING" || selectedStatus === "PAID") && (
              <div>{renderSalaryPayment(selectedPayRun, selectedRun?.id, employerSocialSec)}</div>
            )}
        </Modal.Content>
      </Modal>

      <Modal
        open={isSearchModalOpen}
        onClose={() => {
          setIsSearchModalOpen(false);
        }}
        size="small"
      >
        <Modal.Header>Pay Run Search</Modal.Header>
        <Modal.Content>
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <label style={{ fontWeight: "bold" }}>Issued Date</label>
              <DateTextBox
                placeholder="dd/mm/yyyy"
                value={issuedDateSearch}
                onChange={(dateStr: string) =>
                  setIssuedDateSearch(
                    dateStr && dateStr.includes("/") ? parseBEDateInput(dateStr) : ""
                  )
                }
                inputStyle={{ width: "140px" }}
              />
            </div>
            <ButtonLoadCheck
              color="blue"
              onClick={(e) =>
                handleSearchSalaryPayRun(e, {
                  value: {
                    payRunCode: "",
                    issuedDateStart: formatToBEDate(issuedDateSearch),
                    issuedDateEnd: formatToBEDate(issuedDateSearch),
                    status: "",
                  },
                })
              }
              setProp={props.setProp}
              size="medium"
              fluid={false}
              paramKey={BTN_ACTS.HANDLE_SEARCH_SALARY_PAY_RUN}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.HANDLE_SEARCH_SALARY_PAY_RUN]}
              title="Search"
            />
          </div>

          <SalaryPayRunTable
            data={
              props.PayrollSequence?.searchResultSalaryPayRunList?.map((item: any) => ({
                code: item.pay_run_code,
                issuedDate: item.issued_date,
                status: item.status,
              })) ?? []
            }
            columns={["issuedDate", "payRunCode", "status"]}
            onRowSelect={(code) => setSelectedRowCode(code)}
            selectedRowCode={selectedRowCode ?? undefined}
            PayrollSequence={props.PayrollSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            plainStatus={true}
            cellAlign="left"
          />

          <div style={{ marginTop: "20px", textAlign: "right" }}>
            <Button
              color="green"
              onClick={() => {
                console.log("Selected row code:", selectedRowCode);
                console.log(
                  "Available pay run codes:",
                  props.PayrollSequence?.salaryPayRunList?.map((item: any) => item.pay_run_code)
                );

                if (!selectedRowCode) {
                  alert("Please select a pay run from the table before clicking Select.");
                  return;
                }

                const selected = props.PayrollSequence?.salaryPayRunList?.find(
                  (item: any) =>
                    item.pay_run_code?.trim().toLowerCase() ===
                    selectedRowCode?.trim().toLowerCase()
                );

                if (selected) {
                  setFilteredPayRunCode(selected.pay_run_code);
                  setPayRunCodeSearch(selected.pay_run_code);
                  setIsSearchModalOpen(false);
                } else {
                  alert("Selected pay run code not found. Please select a valid row.");
                  console.warn("No matching pay run code found for", selectedRowCode);
                }
              }}
            >
              Select
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      <Modal open={successModalOpen} size="small" onClose={() => setSuccessModalOpen(false)}>
        <Modal.Header style={{ backgroundColor: "#2baf6a", color: "white" }}>
          Notification Message
        </Modal.Header>
        <Modal.Content>
          <div
            style={{
              fontSize: "18px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              fontWeight: "bold",
            }}
          >
            <p style={{ marginBottom: "1.5em" }}>
              Create Salary Pay Run <span style={{ color: "#2baf6a" }}>Success</span>
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <p style={{ width: "200px", margin: 0 }}>Pay Run Code</p>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "5px",
                  flex: 1,
                  textAlign: "left",
                }}
              >
                <span style={{ marginLeft: "20px", fontWeight: "normal" }}>
                  {lastSubmittedPayRun?.pay_run_code}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <p style={{ width: "200px", margin: 0 }}>Status</p>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "5px",
                  flex: 1,
                  textAlign: "left",
                }}
              >
                <span style={{ marginLeft: "20px", fontWeight: "normal" }}>
                  {statusTextMap[lastSubmittedPayRun?.status]}
                </span>
              </div>
            </div>

            <p style={{ marginTop: "1em" }}>Can check Pay Run details with Salary Pay Run Tab</p>

            <Button
              style={{
                backgroundColor: "white",
                color: "#2baf6a",
                border: "1px solid #2baf6a",
                marginTop: "2em",
                alignSelf: "center",
              }}
              onClick={() => {
                setSuccessModalOpen(false);
                resetCreateSalaryState();
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      <Modal
        open={openCancelReasonModal}
        onClose={() => setOpenCancelReasonModal(false)}
        size="small"
        style={{ top: "50%", transform: "translateY(-50%)" }}
      >
        <Modal.Header style={{ backgroundColor: "red", color: "white" }}>
          Cancel Pay Run
        </Modal.Header>
        <Modal.Content>
          <p style={{ fontWeight: "bold" }}>
            Please provide a reason for cancelling the installment.
          </p>
          <Dropdown
            fluid
            multiple
            search
            selection
            allowAdditions
            additionLabel="Add: "
            options={dropdownOptions}
            value={cancelReason}
            onChange={(_e, { value }) => setCancelReason(value as string[])}
            onAddItem={(_e, { value }) => {
              if (typeof value !== "string") return;

              setCancelReason((prev) => (prev.includes(value) ? prev : [...prev, value]));
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              marginTop: "20px",
            }}
          >
            <Button
              color="green"
              disabled={cancelReason.length === 0 || isDeleting}
              loading={isDeleting}
              onClick={handleSaveCancelReasonAndDelete}
            >
              Confirm
            </Button>
            <Button
              color="red"
              onClick={() => {
                setOpenCancelReasonModal(false);
                setPendingCancelRun(null);
                setCancelReason([]);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      <Modal
        open={cancelModalOpen}
        size="small"
        onClose={() => setCancelModalOpen(false)}
        style={{ top: "50%", transform: "translateY(-50%)" }}
      >
        <Modal.Header style={{ backgroundColor: "#2baf6a", color: "white" }}>
          Notification Message
        </Modal.Header>
        <Modal.Content>
          <div
            style={{
              fontSize: "18px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              fontWeight: "bold",
            }}
          >
            <p style={{ marginBottom: "1.5em" }}>
              Cancel Pay Run Code <span style={{ color: "red" }}>Success</span>
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1em",
              }}
            >
              <p style={{ width: "150px", margin: 0 }}>Pay Run Code</p>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "8px 12px",
                  borderRadius: "5px",
                  flex: 1,
                  textAlign: "left",
                }}
              >
                <span style={{ marginLeft: "20px", fontWeight: "normal" }}>
                  {lastCancelledPayRunCode || "-"}
                </span>
              </div>
            </div>
            <Button
              style={{
                backgroundColor: "white",
                color: "#2baf6a",
                border: "1px solid #2baf6a",
                marginTop: "2em",
                alignSelf: "center",
              }}
              onClick={() => {
                setCancelModalOpen(false);
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      <Modal open={openOtReasonModal} onClose={() => setOpenOtReasonModal(false)} size="small">
        <Modal.Header style={{ backgroundColor: "red", color: "white" }}>
          Alert message
        </Modal.Header>
        <Modal.Content>
          <p style={{ fontWeight: "bold" }}>
            ๐ The sum of the modified overtime exceeds the total approved overtime.
            <br />๐ Please provide a reason if you wish to confirm the OT change.
          </p>
          <TextArea
            value={otReason}
            onChange={(e: any) => setOtReason(e.target.value)}
            style={{ width: "100%" }}
          />
          <div
            style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "20px" }}
          >
            <Button
              color="green"
              disabled={!otReason.trim()}
              onClick={async () => {
                if (!pendingOtEdit) return;

                const payload = {
                  ...pendingOtEdit,
                  ot_reason: otReason,
                };

                await handleEditEmployeePayroll(null, {}, payload);

                setOpenOtReasonModal(false);
                setPendingOtEdit(null);
                setOtReason("");
                setOpenOvertimeModal(false);
                setSelectedEmployee(null);
              }}
            >
              Confirm
            </Button>
            <Button
              color="red"
              onClick={() => {
                setOpenOtReasonModal(false);
                setPendingOtEdit(null);
                setOtReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      <Modal
        open={openRevertReasonModal}
        onClose={() => setOpenRevertReasonModal(false)}
        size="small"
        style={{ top: "50%", transform: "translateY(-50%)" }}
      >
        <Modal.Header style={{ backgroundColor: "red", color: "white" }}>
          Alert Message
        </Modal.Header>
        <Modal.Content>
          <p style={{ fontWeight: "bold" }}>Please provide a reason for reverting the status.</p>
          <Dropdown
            fluid
            multiple
            search
            selection
            allowAdditions
            additionLabel="Add: "
            options={dropdownOptions}
            value={revertReason}
            onChange={(_e, { value }) => setRevertReason(value as string[])}
            onAddItem={(_e, { value }) => {
              if (typeof value !== "string") return;
              setRevertReason((prev) => (prev.includes(value) ? prev : [...prev, value]));
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              marginTop: "20px",
            }}
          >
            <Button
              color="green"
              disabled={revertReason.length === 0 || isReverting}
              loading={isReverting}
              onClick={handleSaveRevertReasonAndEdit}
            >
              Confirm
            </Button>
            <Button
              color="red"
              onClick={() => {
                setOpenRevertReasonModal(false);
                setPendingRevertRun(null);
                setPendingRevertActionKey(null);
                setRevertReason([]);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      {showSnack && (
        <SnackMessage
          onEvent={props.onEvent}
          onClose={handleSnackClose}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
        />
      )}
    </>
  );
};

export default React.memo(CardPayrollSalaryTab);
